// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// ── Two entries in the sidebar, and the split is the module ──────────────────
//
// `/my-calendar` is the worker's screen: one route, one permission, no folder.
// Most of the staff will only ever see this one, and burying it inside a folder
// of six would be making forty people click twice a day to answer «¿cuándo
// trabajo?».
//
// `/workforce` is everything administration does, as a folder — because a
// module with seven screens either takes seven slots in the sidebar, pushing
// whatever is installed next below the fold, or hides six of them behind a page
// somebody has to look around in. Neither is navigation.

export const routes = [
  // The worker's own screen. First, and outside the folder.
  {
    path: '/my-calendar',
    name: 'my-calendar',
    component: () => import('./pages/MyCalendarPage.vue'),
    meta: { permission: 'workforce:me', title: 'workforce.my.title' },
  },

  {
    path: '/workforce',
    name: 'workforce',
    component: () => import('./pages/PlanningPage.vue'),
    meta: { permission: 'workforce:read', title: 'workforce.planning.title' },
  },
  {
    path: '/workforce/team',
    name: 'workforce-team',
    component: () => import('./pages/TeamCalendarPage.vue'),
    meta: { permission: 'workforce:read', title: 'workforce.team.title' },
  },
  {
    path: '/workforce/requests',
    name: 'workforce-requests',
    component: () => import('./pages/RequestsPage.vue'),
    meta: { permission: 'workforce:approve', title: 'workforce.requests.title' },
  },
  {
    path: '/workforce/absences',
    name: 'workforce-absences',
    component: () => import('./pages/AbsencesPage.vue'),
    meta: { permission: 'workforce:read', title: 'workforce.absences.title' },
  },
  {
    path: '/workforce/balances',
    name: 'workforce-balances',
    component: () => import('./pages/BalancesPage.vue'),
    meta: { permission: 'workforce:manage', title: 'workforce.balances.title' },
  },
  {
    path: '/workforce/calendars',
    name: 'workforce-calendars',
    component: () => import('./pages/CalendarsPage.vue'),
    meta: { permission: 'workforce:read', title: 'workforce.calendars.title' },
  },
  {
    path: '/workforce/settings',
    name: 'workforce-settings',
    component: () => import('./pages/ConfigPage.vue'),
    meta: { permission: 'workforce:manage', title: 'workforce.config.title' },
  },
  // La ficha laboral. Una sola pantalla y no lista + detalle: son nueve campos,
  // y una página entera para nueve campos es un clic de más en el camino de
  // quien solo quiere corregir una fecha de alta.
  {
    path: '/workforce/people',
    name: 'workforce-people',
    component: () => import('./pages/PeoplePage.vue'),
    meta: { permission: 'workforce:people', title: 'workforce.people.title' },
  },
]

export const navigation = [
  {
    to: '/my-calendar',
    labelKey: 'workforce.my.title',
    permission: 'workforce:me',
    icon: 'user',
    group: 'business',
  },
  {
    to: '/workforce',
    labelKey: 'workforce.title',
    permission: 'workforce:read',
    icon: 'briefcase',
    group: 'business',
    children: [
      { to: '/workforce', labelKey: 'workforce.planning.title', permission: 'workforce:read' },
      { to: '/workforce/team', labelKey: 'workforce.team.title', permission: 'workforce:read' },
      { to: '/workforce/requests', labelKey: 'workforce.requests.title', permission: 'workforce:approve' },
      { to: '/workforce/absences', labelKey: 'workforce.absences.title', permission: 'workforce:read' },
      { to: '/workforce/people', labelKey: 'workforce.people.title', permission: 'workforce:people' },
      { to: '/workforce/balances', labelKey: 'workforce.balances.title', permission: 'workforce:manage' },
      { to: '/workforce/calendars', labelKey: 'workforce.calendars.title', permission: 'workforce:read' },
      { to: '/workforce/settings', labelKey: 'workforce.config.title', permission: 'workforce:manage' },
    ],
  },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (§16): a module that ships two is a module that cannot be sold
// to the third client.
//
// Note the three blocks at the bottom of each locale — `modules`,
// `permissions`, `settings.ns`, `audit.actions`, `assistant.openers`. They are
// the platform's own screens talking about this module, and they travel with it
// so installing it leaves nothing to add to the shell by hand (§4.2).
const baseMessages = {
  es: {
    workforce: {
      title: 'Plantilla',
      search: { subtitle: 'Cuadrante y expediente' },

      actions: {
        save: 'Guardar', cancel: 'Cancelar', close: 'Cerrar', edit: 'Editar',
        delete: 'Eliminar', archive: 'Archivar', restore: 'Devolver al catálogo',
      },
      state: { active: 'Activo', inactive: 'Inactivo' },
      status: {
        pending: 'Pendiente', approved: 'Aprobada', rejected: 'Rechazada', cancelled: 'Anulada',
      },
      units: { day: 'Día completo', half_day: 'Medio día', hour: 'Por horas' },
      cell: {
        work: 'Turno', rest: 'Libre', absent: 'Ausente',
        requested: 'Solicitado', holiday: 'Festivo', free: 'Sin planificar',
      },
      concepts: { holiday: 'Vacaciones', personal: 'Asuntos propios' },

      // Los tipos que se siembran al instalar. Un cliente que invente el
      // duodécimo lo lee como lo haya llamado él: la clave cae al nombre
      // guardado, igual que el catálogo de errores.
      seed: {
        absenceTypes: {
          holiday: 'Vacaciones', personal: 'Asuntos propios',
          paid_leave: 'Permiso retribuido', unpaid_leave: 'Permiso no retribuido',
          sick: 'Baja médica', accident: 'Accidente laboral',
          parental: 'Maternidad / paternidad', training: 'Formación',
          justified: 'Ausencia justificada', unjustified: 'Ausencia injustificada',
          other: 'Otros',
        },
      },

      conflicts: {
        absence: 'está de {typeName}',
        absence_pending: 'tiene pedido {typeName} sin resolver',
        holiday: 'es festivo ({name})',
        rest: 'solo {hours} h de descanso desde el turno del {otherDate}, y el mínimo son {required}',
        consecutive: 'serían {days} días seguidos, y el máximo son {max}',
      },

      filters: {
        allTeams: 'Todos los equipos', allShifts: 'Todos los turnos',
        allStatuses: 'Todos los estados', allTypes: 'Todos los tipos',
        searchWorker: 'Buscar persona…',
      },

      // ── El cuadrante ────────────────────────────────────────────────────
      planning: {
        title: 'Planificación',
        subtitle: 'Quién trabaja, qué turno y qué hay que cubrir',
        week: 'Semana', month: 'Mes', today: 'Hoy',
        prev: 'Anterior', next: 'Siguiente', jumpTo: 'Ir a una fecha',
        searchPlaceholder: 'Buscar persona…',
        gridLabel: 'Cuadrante por persona y día',
        selectAll: 'Todo', selectRow: 'Seleccionar toda la fila',
        selectColumn: 'Seleccionar todo el día',
        draftLegend: 'Sin publicar',
        publish: 'Publicar',
        publishCount: 'Publicar ({count})',
        published: 'Todo publicado',
        publishTitle: 'Publicar la planificación',
        publishQuestion: 'A partir de ahora {count} turnos del {from} al {to} se verán en «Mi calendario». '
          + 'Quien esté afectado recibe un aviso.',
        publishDone: '{count} turnos publicados para {workers} personas',
      },

      stats: {
        working: 'Trabajando hoy', absent: 'Ausentes', rest: 'Libran',
        unplanned: 'Sin planificar', weekHours: 'Horas de la semana',
      },

      assign: {
        title: 'Asignar',
        selected: '{count} casillas',
        hint: 'Selecciona casillas en el cuadrante: pincha una, arrastra por una fila, o usa la '
          + 'cabecera del día y el nombre para coger una columna o una fila entera.',
        scope: '{people} personas · {days} días',
        shift: 'Turno', pickShift: 'Elige un turno', preview: 'Quedaría',
        apply: 'Asignar', clear: 'Vaciar',
        clearTitle: 'Vaciar las casillas',
        clearQuestion: 'Se quitan {count} turnos. Los que ya estén publicados se quedan.',
        cleared: '{count} casillas vaciadas',
        done: '{count} turnos asignados',
        copyWeek: 'Copiar semana', applyPattern: 'Aplicar plantilla',
        warnings: '{count} avisos',
        andMore: 'y {count} más',
      },

      copy: {
        title: 'Copiar una semana',
        lead: 'Se copia lo planificado del {from} al {to}.',
        target: 'Pegar a partir del', targetHint: 'Tiene que ser posterior a lo que se copia.',
        into: 'Se pegará del {from} al {to}',
        onlySelected: 'Solo las {count} personas seleccionadas',
        confirm: 'Copiar',
        done: '{count} turnos copiados ({replaced} reemplazaban a otros)',
        conflicts: '{count} avisos que conviene mirar',
      },

      pattern: {
        newTitle: 'Nueva plantilla',
        applyTitle: 'Aplicar una plantilla',
        name: 'Nombre', namePlaceholder: 'Horario de oficina, Rotación producción A…',
        description: 'Descripción',
        cycleWeeks: 'Cada cuántas semanas se repite',
        cycleHint: 'Una semana es un horario fijo. Tres, la rotación mañana / tarde / noche.',
        oneWeek: 'Cada semana', weeks: '{count} semanas',
        weekN: 'Semana {week}', everyWeek: 'Todas',
        free: 'Libre', filled: 'Días',
        gridHint: 'Deja en blanco los días que no lleven turno. Un día en blanco es «sin decidir»; '
          + 'para decir «libra», usa un turno de tipo descanso.',
        active: 'Activa',
        pattern: 'Plantilla', pick: 'Elige una plantilla',
        from: 'Desde', to: 'Hasta',
        cycle: 'Rota cada {weeks} semanas',
        everybody: 'Todas las personas ({count})',
        stagger: 'Escalonar',
        startsOnWeek: 'Empieza en la semana {week}',
        preview: 'Ver qué haría',
        previewCount: '{count} turnos para {people} personas',
        previewEmpty: 'La plantilla no pone nada en ese rango',
        apply: 'Aplicar ({count})',
        applied: '{count} turnos generados, {warnings} avisos',
        andMore: 'y {count} más',
        saved: 'Plantilla guardada',
        archiveTitle: 'Archivar la plantilla',
        archiveQuestion: 'Deja de ofrecerse. Los cuadrantes que salieron de ella se quedan como están.',
        restored: 'Plantilla devuelta al catálogo',
        archived: 'Plantilla archivada',
      },

      // ── Turnos ──────────────────────────────────────────────────────────
      shifts: {
        newTitle: 'Nuevo turno',
        name: 'Nombre', namePlaceholder: 'Mañana, Tarde, Noche…',
        code: 'Código', codeHint: 'Una o dos letras: es lo que cabe en la casilla.',
        kind: 'Tipo', kindHint: 'Un turno de descanso es «libra», que no es lo mismo que una casilla vacía.',
        kindWork: 'Se trabaja', kindRest: 'Descanso',
        start: 'Entrada', end: 'Salida', break: 'Pausa (min)',
        hours: 'Horario', duration: 'Duración',
        crossesMidnight: 'Cruza la medianoche',
        overnight: 'nocturno',
        colour: 'Color', description: 'Notas',
        site: 'Centro', siteHint: 'Vacío = cualquiera', allSites: 'Todos',
        active: 'Activo',
        saved: 'Turno guardado',
        archiveTitle: 'Archivar el turno',
        archiveQuestion: 'Deja de ofrecerse al planificar. Lo que ya está en el cuadrante se queda.',
        restored: 'Turno devuelto al catálogo',
        archived: 'Turno archivado',
      },

      // ── Ausencias ───────────────────────────────────────────────────────
      absences: {
        title: 'Ausencias',
        subtitle: 'Quién falta, cuándo y quién lo aprobó',
        new: 'Registrar ausencia',
        overrode: 'Se aprobó dejando el equipo bajo mínimos',
      },

      request: {
        title: 'Solicitar ausencia',
        type: 'Tipo', pickType: 'Elige el tipo',
        from: 'Desde', to: 'Hasta',
        unit: 'Cuánto', fromTime: 'Desde las', toTime: 'Hasta las',
        reason: 'Motivo', notes: 'Comentario',
        calendarDays: 'Días naturales', workingDays: 'Días laborables',
        holidays: 'Festivos', shiftsAffected: 'Turnos afectados',
        chargeable: 'Descuentan', days: 'días',
        balanceBefore: 'Saldo',
        notEnough: 'Te faltan {short} días de saldo. Se puede pedir igualmente; lo decide quien apruebe.',
        overlaps: 'Ya tienes {type} del {from} al {to}. Anula esa antes de pedir estos días.',
        noApproval: 'Este tipo no necesita aprobación: queda registrado directamente.',
        send: 'Enviar solicitud',
        sent: 'Solicitud enviada. Queda pendiente de aprobación.',
        recorded: 'Ausencia registrada',
      },

      requests: {
        title: 'Solicitudes',
        subtitle: 'Lo que está esperando una respuesta',
        waiting: 'Pendientes', late: 'Fuera de plazo',
        worker: 'Persona',
        overdue: 'Fuera de plazo', imminent: 'Empieza pronto',
        requestedBy: 'La pide', themselves: 'La persona',
        fileFor: 'Registrar por alguien',
      },

      approve: {
        balance: 'Saldo',
        balanceOf: 'de {concept}',
        overdrawn: 'Se quedaría con {days} días de más sobre su saldo.',
        noTeam: 'Sin equipo',
        noRule: 'Este equipo no tiene cobertura mínima configurada.',
        checking: 'Mirando la cobertura…',
        fine: 'La cobertura aguanta todos los días.',
        breach: 'Aprobarlo deja el equipo bajo mínimos {days} días.',
        alsoAway: 'También faltan: {names}',
        notes: 'Comentario', notesHint: 'Lo verá quien lo pidió.',
        acceptBreach: 'He leído la cobertura y lo apruebo igualmente',
        acceptBreachHint: 'Queda registrado a tu nombre y se avisa por el bus, para que quien '
          + 'planifique la semana lo sepa.',
        blocked: 'Esto dejaría el equipo bajo mínimos. Míralo antes de decidir.',
        approve: 'Aprobar', reject: 'Rechazar',
        approvedDone: 'Ausencia aprobada', rejectedDone: 'Solicitud rechazada',
        selfApproval: 'Nadie decide sus propias ausencias: tiene que hacerlo otra persona con permiso para aprobar.',
        alreadyDecided: 'Otra persona acaba de decidir esta ausencia. Recarga para ver cómo ha quedado.',
        alreadyStarted: 'Esta ausencia ya ha empezado y no se puede retirar. Pídeselo a quien aprueba.',
      },

      // ── Cobertura ───────────────────────────────────────────────────────
      coverage: {
        title: 'Cobertura',
        short: 'Días bajo mínimos',
        newTitle: 'Nueva cobertura mínima',
        status: {
          ok: 'Cobertura correcta', at_minimum: 'Justo el mínimo',
          below: 'Cobertura insuficiente', no_rule: 'Sin regla',
        },
        away: 'Faltan: {names}',
        scope: 'Sobre qué', scopeHint: 'Un equipo si tienes el módulo de equipos; si no, un centro.',
        scopeTeam: 'Equipo', scopeSite: 'Centro',
        team: 'Equipo', site: 'Centro', target: 'Sobre',
        pickTarget: 'Elige',
        shift: 'Turno', shiftHint: 'Vacío = cuenta a todo el mundo, esté en el turno que esté.',
        anyShift: 'Cualquiera',
        minimum: 'Mínimo',
        policy: 'Qué pasa si se baja del mínimo',
        policies: { warn: 'Solo avisar', confirm: 'Pedir confirmación', block: 'Bloquear' },
        policyHint: {
          warn: 'Se ve el número y se aprueba igual. Sirve cuando el mínimo es orientativo.',
          confirm: 'Hay que marcar una casilla diciendo que se ha leído. Queda registrado a nombre '
            + 'de quien aprueba, y es la opción que de verdad cambia la conducta.',
          block: 'No se puede aprobar. Úsalo solo donde el mínimo sea legal o de seguridad.',
        },
        weekdays: 'Días en que aplica', weekdaysHint: 'Sin marcar nada, aplica todos los días.',
        saved: 'Cobertura guardada',
        removeTitle: 'Quitar la cobertura mínima',
        removeQuestion: 'El equipo deja de comprobarse al aprobar ausencias.',
        removed: 'Cobertura eliminada',
      },

      // ── Mi calendario ───────────────────────────────────────────────────
      my: {
        title: 'Mi calendario',
        subtitle: 'Cuándo trabajo y cuántos días me quedan',
        today: 'Hoy', todayShort: 'Hoy', tomorrow: 'Mañana',
        next: 'Próximos turnos',
        balance: 'Mis días',
        pendingDays: '{count} pendientes de aprobar',
        request: 'Solicitar ausencia',
      },

      day: {
        shift: 'Turno', hours: 'Horario', duration: 'Duración',
        site: 'Centro', notes: 'Notas',
        absence: 'Ausencia', range: 'Fechas', state: 'Estado', holiday: 'Festivo',
      },

      // ── Calendarios laborales ───────────────────────────────────────────
      calendars: {
        title: 'Calendarios laborales',
        subtitle: 'Los días que no se trabaja, por centro y por año',
        new: 'Nuevo calendario', newTitle: 'Nuevo calendario laboral',
        name: 'Nombre', namePlaceholder: 'Calendario Puigcerdà',
        year: 'Año', holidays: 'Festivos',
        site: 'Centro', siteHint: 'Vacío = toda la empresa', allSites: 'Toda la empresa',
        isDefault: 'Es el calendario por defecto',
        isDefaultHint: 'Lo usa quien no tenga otro asignado. Solo uno por año.',
        default: 'Por defecto',
        date: 'Fecha', holidayName: 'Qué se celebra', unnamed: 'Sin nombre',
        add: 'Añadir', noHolidays: 'Todavía no hay festivos. Añádelos uno a uno mirando el BOE '
          + 'y el boletín de la comunidad: cambian cada año y por eso no los rellenamos nosotros.',
        scopes: { national: 'Nacional', regional: 'Autonómico', local: 'Local', company: 'De empresa' },
        preview: 'Festivos de {name}',
        copyForward: 'Copiar a {year}',
        copied: 'Calendario de {year} creado. Los festivos se meten a mano: cambian cada año.',
        saved: 'Calendario guardado',
      },

      // ── Equipo ──────────────────────────────────────────────────────────
      team: {
        title: 'Calendario de equipo',
        subtitle: 'La semana de un equipo, con la cobertura debajo',
      },

      // ── Saldos ──────────────────────────────────────────────────────────
      balances: {
        title: 'Saldos',
        subtitle: 'Cuántos días tiene cada persona y cuántos ha gastado',
        entitled: 'Asignados', consumed: 'Consumidos',
        pending: 'Pendientes', available: 'Disponibles',
        allocated: 'Asignación del año', carriedOver: 'Vienen del año anterior',
        carriedOverHint: 'Solo si el convenio deja arrastrarlos.',
        accrualFrom: 'Con derecho desde', accrualTo: 'Hasta',
        accrualHint: 'Para altas y bajas a mitad de año: la asignación se prorratea por días.',
        allocate: 'Asignar días', allocateTitle: 'Asignación anual',
        adjust: 'Ajustar', adjustTitle: 'Ajustar el saldo',
        currently: 'Ahora mismo tiene {days} días',
        days: 'Días', daysPlaceholder: '+1 o -1',
        reason: 'Motivo', reasonHint: 'Obligatorio. Lo leerá alguien que no estaba, dentro de dos años.',
        adjusted: 'Saldo ajustado',
        openFile: 'Abrir el expediente',
        rollOver: 'Pasar al año siguiente', rollOverTitle: 'Pasar los saldos al año siguiente',
        rollOverLead: 'Se copian las asignaciones de {from} a {to}. Los ajustes no se copian: '
          + 'eran de aquel año.',
        carryUnused: 'Arrastrar los días que sobran',
        carryUnusedHint: 'Si el convenio no lo dice, déjalo desactivado: nadie arrastra días por '
          + 'defecto, y regalarlos es más difícil de deshacer que concederlos después.',
        rolledOver: '{count} saldos creados para {year}',
      },

      // ── La ficha laboral ────────────────────────────────────────────────
      people: {
        title: 'Personal',
        subtitle: 'Los datos de empleo que el cuadrante necesita para dibujarse',
        person: 'Persona', team: 'Equipo', site: 'Centro',
        code: 'Nº', contract: 'Contrato', category: 'Categoría', manager: 'Responsable',
        total: 'Personas', withEnd: 'Con baja prevista', unassigned: 'Sin categoría',
        openEnded: 'Indefinido', noContract: 'Sin datos',
        hoursShort: '{hours} h/sem',
        since: 'Desde el {date}', until: 'Hasta el {date}',
        endsIn: 'Acaba en {days} días', ended: 'Terminado',
        allCategories: 'Todas las categorías',
        edit: 'Editar la ficha', schedule: 'Ver su cuadrante',
        managed: 'Lo lleva Expediente',
        managedHint: 'Con el módulo de expediente instalado, estos datos se editan allí y aquí '
          + 'solo se leen. Dos sitios escribiendo la misma fecha de alta es como dejan de '
          + 'coincidir.',
      },

      person: {
        title: 'Ficha laboral',
        editTitle: 'Datos laborales',
        code: 'Nº de empleado',
        contractType: 'Tipo de contrato',
        contractFrom: 'Alta', contractTo: 'Baja prevista',
        contractToHint: 'Vacío = indefinido. Fuera de estas fechas no se planifica.',
        weeklyHours: 'Horas semanales',
        weeklyHoursHint: 'Es lo que convierte una ausencia pedida por horas en fracción de día.',
        workingWeek: 'Su semana laboral',
        workingWeekHint: 'Sin marcar nada se usa la de la empresa. Solo para quien no tenga cuadrante.',
        category: 'Categoría',
        categoryHint: 'Cómo se sabe de un vistazo quién puede cubrir un turno.',
        manager: 'Responsable',
        managerHint: 'A quien le llegan sus solicitudes de ausencia.',
        noManager: 'Nadie en concreto',
        workCalendar: 'Calendario laboral',
        workCalendarHint: 'Vacío = el calendario por defecto del año.',
        defaultCalendar: 'El de por defecto',
        notes: 'Notas', saved: 'Ficha guardada',
      },




      absenceTypes: {
        newTitle: 'Nuevo tipo de ausencia',
        name: 'Nombre', code: 'Código',
        codeHint: 'No se puede cambiar después.',
        codeLocked: 'No se cambia: las ausencias ya guardadas lo llevan.',
        colour: 'Color',
        needs: 'Qué hace falta', costs: 'Qué cuesta',
        allows: 'Cómo se puede pedir', affects: 'A qué afecta',
        requiresApproval: 'Alguien tiene que aprobarla',
        requiresReason: 'Hay que explicar el motivo',
        requiresDocument: 'Hay que adjuntar un justificante',
        approvalDays: 'Plazo para responder (días)',
        approvalDaysHint: 'Sirve para ordenar la bandeja: lo que pasa de plazo sube arriba.',
        consumesBalance: 'Descuenta días de un saldo',
        balance: 'Saldo',
        balanceConcept: 'De qué saldo',
        balanceConceptHint: 'Un código. Dos tipos con el mismo código comparten bolsa.',
        isPaid: 'Es retribuida',
        allowsHalfDay: 'Se puede pedir medio día',
        allowsHours: 'Se puede pedir por horas',
        affectsPlanning: 'Quita a la persona del cuadrante',
        affectsPlanningHint: 'Desactívalo en formación: quien está en un curso está trabajando, y '
          + 'marcarlo como ausente hace que el equipo parezca corto sin serlo.',
        affectsAttendance: 'Fichajes deja de esperar su fichaje',
        affectsAttendanceHint: 'Es lo que evita que unas vacaciones salgan como falta de fichaje. '
          + 'Desactívalo solo en la ausencia injustificada: que no vino es justo lo que el '
          + 'registro de jornada tiene que seguir diciendo.',
        active: 'Activo', saved: 'Tipo guardado',
        short: {
          approval: 'aprobación', document: 'justificante',
          works: 'sigue en cuadrante', attendance: 'cuenta en fichajes',
        },
      },

      // ── Configuración ───────────────────────────────────────────────────
      config: {
        title: 'Configuración',
        subtitle: 'Lo que se decide una vez: turnos, plantillas, tipos y cobertura',
        showArchived: 'Ver archivados',
        shifts: 'Turnos', patterns: 'Plantillas',
        absenceTypes: 'Tipos de ausencia',
        coverage: 'Cobertura',
        new: {
          shifts: 'Nuevo turno', patterns: 'Nueva plantilla',
          absenceTypes: 'Nuevo tipo',
          coverage: 'Nueva cobertura',
        },
      },

      // ── Estados vacíos ──────────────────────────────────────────────────
      empty: {
        noShifts: 'Todavía no hay turnos configurados',
        noShiftsHint: 'Un turno es un tramo del reloj con nombre: «Mañana, de 06:00 a 14:00». '
          + 'Sin al menos uno no se puede planificar nada.',
        createShift: 'Crear el primer turno',
        noPeople: 'No hay nadie en el cuadrante',
        noPeopleHint: 'Las personas salen de los usuarios de la plataforma. Comprueba en los '
          + 'ajustes qué roles aparecen aquí.',
        noPatterns: 'Todavía no hay plantillas',
        noPatternsHint: 'Una plantilla es la forma de un cuadrante sin fechas ni personas. Es lo '
          + 'que convierte planificar un mes para doce personas en una pulsación.',
        noRequests: 'No hay solicitudes pendientes',
        noRequestsHint: 'Cuando alguien pida vacaciones o un permiso, aparecerá aquí.',
        noAbsences: 'No hay ausencias en este período',
        noAbsencesHint: 'Prueba a ampliar las fechas o a quitar algún filtro.',
        noAbsencesForPerson: 'Esta persona no tiene ausencias registradas',
        noShiftsForMe: 'No tienes turnos planificados',
        noShiftsForMeHint: 'En cuanto se publique el cuadrante aparecerán aquí.',
        noShiftsForPerson: 'Sin turnos en este período',
        noBalance: 'Todavía no tienes saldos asignados',
        noBalanceHint: 'Los asigna quien lleve el personal, una vez al año.',
        noBalances: 'Nadie tiene saldo de este concepto',
        noBalancesHint: 'Asígnalos persona a persona, o pasa los del año anterior.',
        noCalendars: 'Todavía no hay calendarios laborales',
        noCalendarsHint: 'Un calendario son los festivos de un centro y un año. Sin él, un festivo '
          + 'se planifica como un día cualquiera.',
        noCoverage: 'No hay coberturas mínimas configuradas',
        noCoverageHint: 'Es lo que hace que aprobar unas vacaciones enseñe con cuánta gente se '
          + 'queda el equipo, en vez de dos botones y nada más.',
        noTeam: 'Elige un equipo',
        noTeamHint: 'Los equipos vienen del módulo de Equipos. Sin él, configura la cobertura '
          + 'sobre un centro.',
      },

      // ── Avisos ──────────────────────────────────────────────────────────
      notifications: {
        categories: {
          published: 'Cuadrante publicado',
          requested: 'Solicitudes de ausencia',
          decided: 'Respuesta a tu solicitud',
          coverage: 'Un turno por debajo del mínimo',
        },
        // El que se arregla llamando a alguien HOY: el jueves que viene
        // ya no se cubre.
        coverage: {
          title: 'Un turno se queda por debajo del mínimo',
          body: '{days} día(s) bajo mínimo. El {date}: {available} de {required}.',
        },
        published: {
          title: 'Ya está el cuadrante',
          body: 'Se ha publicado la planificación del {from} al {to}.',
        },
        absence: {
          requested: {
            title: 'Solicitud de {type}',
            body: '{worker} pide del {from} al {to}.',
          },
          approved: { title: 'Aprobada', body: 'Tu {type} del {from} al {to} está aprobada.' },
          rejected: { title: 'Rechazada', body: 'Tu {type} del {from} al {to} no se ha aprobado.' },
          cancelled: { title: 'Anulada', body: 'Se ha anulado tu {type} del {from} al {to}.' },
        },
        unplanned: {
          title: 'Huecos la semana que viene',
          body: '{count} personas no tienen nada puesto del {from} al {to}.',
        },
      },

      // Las tarjetas del asistente (backend/assistant.js): pedir, aprobar y
      // rechazar una ausencia.
      assistant: {
        request: {
          summary: 'Pedir {type} del {from} al {to}',
          action: 'Pedir una ausencia',
          after: 'Queda pendiente de que alguien la apruebe',
          outcome: 'Pedido: {type} del {from} al {to}',
        },
        field: {
          type: 'Tipo',
          from: 'Del',
          to: 'Al',
          reason: 'Motivo',
          who: 'Quién',
          days: 'Días',
          notes: 'Nota',
        },
        approve: {
          summary: 'Aprobar {type} de {who} del {from} al {to}',
          action: 'Aprobar ausencia',
          outcome: 'Aprobada: {who}, del {from} al {to}',
          breach: 'Se aprueba aunque el equipo quede por debajo del mínimo',
        },
        reject: {
          summary: 'Rechazar {type} de {who} del {from} al {to}',
          action: 'Rechazar ausencia',
          outcome: 'Rechazada: {who}, del {from} al {to}',
        },
        decide: {
          notFound: 'No está claro qué solicitud es: elígela en Solicitudes',
          where: 'Solicitud',
        },
      },

      dashboard: {
        mine: 'Mis próximos turnos',
        mineHelp: 'Los días que trabajas a partir de hoy, con su horario. Solo lo publicado.',
        myBalance: 'Mis días',
        myBalanceHelp: 'Lo que te queda de cada bolsa: vacaciones, asuntos propios, lo que haya.',
        workingToday: 'Trabajando hoy',
        workingTodayHelp: 'Cuántas personas tienen turno hoy y no están de baja ni de vacaciones.',
        absentToday: 'Ausentes hoy',
        absentTodayHelp: 'Cuántas ausencias aprobadas cubren el día de hoy.',
        unplanned: 'Sin planificar hoy',
        unplannedHelp: 'Personas de las que hoy no se dice nada: ni turno, ni descanso, ni ausencia.',
        pending: 'Solicitudes pendientes',
        pendingHelp: 'Cuántas ausencias esperan que alguien las apruebe o las rechace.',
        coverage: 'Equipos bajo mínimos',
        coverageHelp: 'Cuántas coberturas mínimas no se cumplen hoy.',
        upcoming: 'Ausencias que llegan',
        upcomingHelp: 'Lo aprobado que empieza en los próximos días, para que dé tiempo a cubrirlo.',
      },
    },

    // ── Cómo se llama este módulo en las pantallas de la plataforma ────────
    modules: { workforce: 'Plantilla' },
    assistant: {
      openers: {
        workforce: {
          'when-do-i-work': '¿Cuándo trabajo esta semana?',
          'my-holidays': '¿Cuántos días de vacaciones me quedan?',
          'who-is-available': '¿Con quién cuento el lunes que viene?',
          'pending-requests': '¿Qué solicitudes de ausencia me esperan?',
        },
      },
    },
    permissions: {
      workforce: {
        me: 'Ver mi cuadrante y pedir ausencias',
        read: 'Ver el cuadrante y las ausencias de todos',
        plan: 'Repartir turnos y publicar el cuadrante',
        approve: 'Aprobar y rechazar ausencias',
        manage: 'Configurar turnos, calendarios, tipos y saldos',
        people: 'Ver y editar la ficha laboral',
      },
    },
    settings: {
      ns: {
        workforce: {
          title: 'Personal',
          subtitle: 'Cómo se planifica, cómo se cuentan las ausencias y quién sale en el cuadrante',
          groups: { planning: 'Cuadrante', absences: 'Ausencias', people: 'Personas' },
          weekStartsOn: {
            label: 'El primer día de la semana',
            help: 'Decide por dónde empieza la rejilla y sobre qué semanas rota una plantilla.',
          },
          publishBeforeVisible: {
            label: 'El cuadrante no se ve hasta publicarlo',
            help: 'Activado, se prepara el mes que viene sin que nadie lo vea. Desactivado, cada '
              + 'cambio aparece al momento en «Mi calendario».',
          },
          minRestHours: {
            label: 'Horas de descanso mínimo entre dos turnos',
            help: 'Doce es lo que pide el Estatuto. Es un aviso, no una prohibición.',
          },
          maxConsecutiveDays: {
            label: 'Días seguidos de trabajo antes de avisar',
            help: 'También un aviso. A cero, no se comprueba.',
          },
          workingWeek: {
            label: 'Días laborables de la semana',
            help: '1 = lunes. Es a lo que se recurre para contar una '
              + 'ausencia de quien no tiene cuadrante.',
          },
          standardDayMinutes: {
            label: 'Minutos de una jornada estándar',
            help: 'Convierte una ausencia pedida por horas en fracción de día.',
          },
          selfRequestAbsence: {
            label: 'Cada persona puede pedir sus ausencias',
            help: 'Desactivado, solo las mete quien tiene permiso para aprobarlas.',
          },
          approverRoles: {
            label: 'Roles que reciben aviso de una solicitud',
            help: 'Además del responsable de la persona, si lo tiene.',
          },
          contractTypes: {
            label: 'Tipos de contrato de esta empresa',
            help: 'Son los que ofrece el selector de la ficha laboral.',
          },
          categories: {
            label: 'Categorías profesionales',
            help: 'Es como se sabe de un vistazo quién puede cubrir un turno.',
          },
          workerRoles: {
            label: 'Roles que aparecen en el cuadrante',
            help: 'De ahí sale la lista de personas al instalar.',
          },
          warnUnplannedWeek: {
            label: 'Avisar los viernes si la semana que viene tiene huecos',
            help: 'Un aviso a quien planifica con la lista de quien no tiene nada puesto.',
          },
        },
      },
    },
    audit: {
      actions: {
        shift: {
          created: 'Turno creado',
          updated: 'Turno modificado',
          archived: 'Turno archivado',
          restored: 'Turno devuelto al catálogo',
        },
        pattern: {
          created: 'Plantilla creada',
          updated: 'Plantilla modificada',
          archived: 'Plantilla archivada',
          restored: 'Plantilla devuelta al catálogo',
        },
        calendar: {
          created: 'Calendario laboral creado',
          updated: 'Calendario laboral modificado',
          archived: 'Calendario laboral archivado',
          restored: 'Calendario laboral devuelto al catálogo',
        },
        absence_type: {
          created: 'Tipo de ausencia creado',
          updated: 'Tipo de ausencia modificado',
          deactivated: 'Tipo de ausencia desactivado',
        },
        plan: {
          assigned: 'Turnos asignados',
          pattern_applied: 'Plantilla aplicada',
          copied: 'Semana copiada',
          cleared: 'Planificación vaciada',
          published: 'Cuadrante publicado',
          publish_notice_failed: 'Fallo al avisar de la publicación',
        },
        absence: {
          requested: 'Ausencia solicitada',
          created: 'Ausencia registrada',
          updated: 'Ausencia modificada',
          approved: 'Ausencia aprobada',
          rejected: 'Ausencia rechazada',
          cancelled: 'Ausencia anulada',
        },
        balance: {
          allocated: 'Saldo asignado',
          adjusted: 'Saldo ajustado',
          rolled_over: 'Saldos pasados al año siguiente',
        },
        coverage: {
          set: 'Cobertura mínima configurada',
          removed: 'Cobertura mínima eliminada',
          overridden: 'Aprobado bajo la cobertura mínima',
        },
        people: {
          record_updated: 'Datos laborales modificados',
        },
      },
    },
    errors: {
      fields: {
        MANAGED_BY_PERSONNEL: 'Esta ficha la lleva el módulo de expediente. Se edita allí.',
        INVALID_TIME: 'Escríbelo como HH:MM',
        INVALID_DATE: 'Eso no es una fecha',
        ENDS_BEFORE_IT_STARTS: 'No puede acabar antes de empezar',
        BREAK_TOO_LONG: 'La pausa no puede durar tanto como el turno',
        RANGE_TOO_LONG: 'El rango es demasiado largo (máximo {max})',
        PARTIAL_NEEDS_ONE_DAY: 'Una ausencia parcial es de un solo día',
        UNIT_NOT_ALLOWED: 'Este tipo de ausencia no se puede pedir así',
        NOTHING_TO_ASSIGN: 'No hay nada que asignar',
        NOTHING_TO_COPY: 'No hay nada planificado que copiar',
        OVERLAPS_SOURCE: 'Tiene que ser posterior a lo que se copia',
        SHIFT_NOT_FOUND: 'Apunta a un turno que ya no existe',
        TYPE_NOT_FOUND: 'Ese tipo no está disponible',
        WRONG_YEAR: 'Esa fecha no es de {year}',
        TOO_MANY: 'Son demasiados (máximo {max})',
      },
    },
  },

  ca: {
    workforce: {
      title: 'Plantilla',
      search: { subtitle: 'Quadrant i expedient' },

      actions: {
        save: 'Desar', cancel: 'Cancel·lar', close: 'Tancar', edit: 'Editar',
        delete: 'Eliminar', archive: 'Arxivar', restore: 'Retornar al catàleg',
      },
      state: { active: 'Actiu', inactive: 'Inactiu' },
      status: {
        pending: 'Pendent', approved: 'Aprovada', rejected: 'Rebutjada', cancelled: 'Anul·lada',
      },
      units: { day: 'Dia sencer', half_day: 'Mig dia', hour: 'Per hores' },
      cell: {
        work: 'Torn', rest: 'Lliure', absent: 'Absent',
        requested: 'Sol·licitat', holiday: 'Festiu', free: 'Sense planificar',
      },
      concepts: { holiday: 'Vacances', personal: 'Assumptes propis' },

      seed: {
        absenceTypes: {
          holiday: 'Vacances', personal: 'Assumptes propis',
          paid_leave: 'Permís retribuït', unpaid_leave: 'Permís no retribuït',
          sick: 'Baixa mèdica', accident: 'Accident laboral',
          parental: 'Maternitat / paternitat', training: 'Formació',
          justified: 'Absència justificada', unjustified: 'Absència injustificada',
          other: 'Altres',
        },
      },

      conflicts: {
        absence: 'està de {typeName}',
        absence_pending: 'té demanat {typeName} sense resoldre',
        holiday: 'és festiu ({name})',
        rest: 'només {hours} h de descans des del torn del {otherDate}, i el mínim són {required}',
        consecutive: 'serien {days} dies seguits, i el màxim són {max}',
      },

      filters: {
        allTeams: 'Totes les colles', allShifts: 'Tots els torns',
        allStatuses: 'Tots els estats', allTypes: 'Tots els tipus',
        searchWorker: 'Cerca una persona…',
      },

      planning: {
        title: 'Planificació',
        subtitle: 'Qui treballa, quin torn i què cal cobrir',
        week: 'Setmana', month: 'Mes', today: 'Avui',
        prev: 'Anterior', next: 'Següent', jumpTo: 'Vés a una data',
        searchPlaceholder: 'Cerca una persona…',
        gridLabel: 'Quadrant per persona i dia',
        selectAll: 'Tot', selectRow: 'Selecciona tota la fila',
        selectColumn: 'Selecciona tot el dia',
        draftLegend: 'Sense publicar',
        publish: 'Publica',
        publishCount: 'Publica ({count})',
        published: 'Tot publicat',
        publishTitle: 'Publicar la planificació',
        publishQuestion: 'A partir d\'ara {count} torns del {from} al {to} es veuran a «El meu '
          + 'calendari». Qui estigui afectat rep un avís.',
        publishDone: '{count} torns publicats per a {workers} persones',
      },

      stats: {
        working: 'Treballant avui', absent: 'Absents', rest: 'Lliuren',
        unplanned: 'Sense planificar', weekHours: 'Hores de la setmana',
      },

      assign: {
        title: 'Assignar',
        selected: '{count} caselles',
        hint: 'Selecciona caselles al quadrant: clica\'n una, arrossega per una fila, o fes servir '
          + 'la capçalera del dia i el nom per agafar una columna o una fila sencera.',
        scope: '{people} persones · {days} dies',
        shift: 'Torn', pickShift: 'Tria un torn', preview: 'Quedaria',
        apply: 'Assigna', clear: 'Buida',
        clearTitle: 'Buidar les caselles',
        clearQuestion: 'Es treuen {count} torns. Els que ja estiguin publicats es queden.',
        cleared: '{count} caselles buidades',
        done: '{count} torns assignats',
        copyWeek: 'Copia la setmana', applyPattern: 'Aplica una plantilla',
        warnings: '{count} avisos',
        andMore: 'i {count} més',
      },

      copy: {
        title: 'Copiar una setmana',
        lead: 'Es copia el que hi ha planificat del {from} al {to}.',
        target: 'Enganxa a partir del', targetHint: 'Ha de ser posterior al que es copia.',
        into: 'S\'enganxarà del {from} al {to}',
        onlySelected: 'Només les {count} persones seleccionades',
        confirm: 'Copia',
        done: '{count} torns copiats ({replaced} en reemplaçaven d\'altres)',
        conflicts: '{count} avisos que convé mirar',
      },

      pattern: {
        newTitle: 'Nova plantilla',
        applyTitle: 'Aplicar una plantilla',
        name: 'Nom', namePlaceholder: 'Horari d\'oficina, Rotació producció A…',
        description: 'Descripció',
        cycleWeeks: 'Cada quantes setmanes es repeteix',
        cycleHint: 'Una setmana és un horari fix. Tres, la rotació matí / tarda / nit.',
        oneWeek: 'Cada setmana', weeks: '{count} setmanes',
        weekN: 'Setmana {week}', everyWeek: 'Totes',
        free: 'Lliure', filled: 'Dies',
        gridHint: 'Deixa en blanc els dies que no portin torn. Un dia en blanc és «sense decidir»; '
          + 'per dir «lliura», fes servir un torn de tipus descans.',
        active: 'Activa',
        pattern: 'Plantilla', pick: 'Tria una plantilla',
        from: 'Des de', to: 'Fins a',
        cycle: 'Rota cada {weeks} setmanes',
        everybody: 'Totes les persones ({count})',
        stagger: 'Esglaona',
        startsOnWeek: 'Comença a la setmana {week}',
        preview: 'Mira què faria',
        previewCount: '{count} torns per a {people} persones',
        previewEmpty: 'La plantilla no posa res en aquest rang',
        apply: 'Aplica ({count})',
        applied: '{count} torns generats, {warnings} avisos',
        andMore: 'i {count} més',
        saved: 'Plantilla desada',
        archiveTitle: 'Arxivar la plantilla',
        archiveQuestion: 'Deixa d\'oferir-se. Els quadrants que en van sortir es queden com estan.',
        restored: 'Plantilla retornada al catàleg',
        archived: 'Plantilla arxivada',
      },

      shifts: {
        newTitle: 'Nou torn',
        name: 'Nom', namePlaceholder: 'Matí, Tarda, Nit…',
        code: 'Codi', codeHint: 'Una o dues lletres: és el que cap a la casella.',
        kind: 'Tipus', kindHint: 'Un torn de descans és «lliura», que no és el mateix que una casella buida.',
        kindWork: 'Es treballa', kindRest: 'Descans',
        start: 'Entrada', end: 'Sortida', break: 'Pausa (min)',
        hours: 'Horari', duration: 'Durada',
        crossesMidnight: 'Creua la mitjanit',
        overnight: 'nocturn',
        colour: 'Color', description: 'Notes',
        site: 'Centre', siteHint: 'Buit = qualsevol', allSites: 'Tots',
        active: 'Actiu',
        saved: 'Torn desat',
        archiveTitle: 'Arxivar el torn',
        archiveQuestion: 'Deixa d\'oferir-se en planificar. El que ja hi ha al quadrant es queda.',
        restored: 'Torn retornat al catàleg',
        archived: 'Torn arxivat',
      },

      absences: {
        title: 'Absències',
        subtitle: 'Qui falta, quan i qui ho va aprovar',
        new: 'Registrar absència',
        overrode: 'Es va aprovar deixant la colla sota mínims',
      },

      request: {
        title: 'Sol·licitar absència',
        type: 'Tipus', pickType: 'Tria el tipus',
        from: 'Des de', to: 'Fins a',
        unit: 'Quant', fromTime: 'Des de les', toTime: 'Fins a les',
        reason: 'Motiu', notes: 'Comentari',
        calendarDays: 'Dies naturals', workingDays: 'Dies laborables',
        holidays: 'Festius', shiftsAffected: 'Torns afectats',
        chargeable: 'Descompten', days: 'dies',
        balanceBefore: 'Saldo',
        notEnough: 'Et falten {short} dies de saldo. Es pot demanar igualment; ho decideix qui aprovi.',
        overlaps: 'Ja tens {type} del {from} al {to}. Anul·la aquella abans de demanar aquests dies.',
        noApproval: 'Aquest tipus no necessita aprovació: queda registrat directament.',
        send: 'Envia la sol·licitud',
        sent: 'Sol·licitud enviada. Queda pendent d\'aprovació.',
        recorded: 'Absència registrada',
      },

      requests: {
        title: 'Sol·licituds',
        subtitle: 'El que està esperant una resposta',
        waiting: 'Pendents', late: 'Fora de termini',
        worker: 'Persona',
        overdue: 'Fora de termini', imminent: 'Comença aviat',
        requestedBy: 'La demana', themselves: 'La persona',
        fileFor: 'Registrar per algú',
      },

      approve: {
        balance: 'Saldo',
        balanceOf: 'de {concept}',
        overdrawn: 'Es quedaria amb {days} dies de més sobre el seu saldo.',
        noTeam: 'Sense colla',
        noRule: 'Aquesta colla no té cobertura mínima configurada.',
        checking: 'Mirant la cobertura…',
        fine: 'La cobertura aguanta tots els dies.',
        breach: 'Aprovar-ho deixa la colla sota mínims {days} dies.',
        alsoAway: 'També falten: {names}',
        notes: 'Comentari', notesHint: 'Ho veurà qui ho va demanar.',
        acceptBreach: 'He llegit la cobertura i ho aprovo igualment',
        acceptBreachHint: 'Queda registrat al teu nom i s\'avisa pel bus, perquè qui planifiqui la '
          + 'setmana ho sàpiga.',
        blocked: 'Això deixaria la colla sota mínims. Mira-ho abans de decidir.',
        approve: 'Aprova', reject: 'Rebutja',
        approvedDone: 'Absència aprovada', rejectedDone: 'Sol·licitud rebutjada',
        selfApproval: 'Ningú decideix les seves pròpies absències: ho ha de fer una altra persona amb permís per aprovar.',
        alreadyDecided: 'Una altra persona acaba de decidir aquesta absència. Recarrega per veure com ha quedat.',
        alreadyStarted: 'Aquesta absència ja ha començat i no es pot retirar. Demana-ho a qui aprova.',
      },

      coverage: {
        title: 'Cobertura',
        short: 'Dies sota mínims',
        newTitle: 'Nova cobertura mínima',
        status: {
          ok: 'Cobertura correcta', at_minimum: 'Just el mínim',
          below: 'Cobertura insuficient', no_rule: 'Sense regla',
        },
        away: 'Falten: {names}',
        scope: 'Sobre què', scopeHint: 'Una colla si tens el mòdul d\'equips; si no, un centre.',
        scopeTeam: 'Colla', scopeSite: 'Centre',
        team: 'Colla', site: 'Centre', target: 'Sobre',
        pickTarget: 'Tria',
        shift: 'Torn', shiftHint: 'Buit = compta tothom, sigui al torn que sigui.',
        anyShift: 'Qualsevol',
        minimum: 'Mínim',
        policy: 'Què passa si es baixa del mínim',
        policies: { warn: 'Només avisar', confirm: 'Demanar confirmació', block: 'Bloquejar' },
        policyHint: {
          warn: 'Es veu el número i s\'aprova igual. Serveix quan el mínim és orientatiu.',
          confirm: 'Cal marcar una casella dient que s\'ha llegit. Queda registrat a nom de qui '
            + 'aprova, i és l\'opció que de debò canvia la conducta.',
          block: 'No es pot aprovar. Fes-ho servir només on el mínim sigui legal o de seguretat.',
        },
        weekdays: 'Dies en què aplica', weekdaysHint: 'Sense marcar res, aplica tots els dies.',
        saved: 'Cobertura desada',
        removeTitle: 'Treure la cobertura mínima',
        removeQuestion: 'La colla deixa de comprovar-se en aprovar absències.',
        removed: 'Cobertura eliminada',
      },

      my: {
        title: 'El meu calendari',
        subtitle: 'Quan treballo i quants dies em queden',
        today: 'Avui', todayShort: 'Avui', tomorrow: 'Demà',
        next: 'Propers torns',
        balance: 'Els meus dies',
        pendingDays: '{count} pendents d\'aprovar',
        request: 'Sol·licitar absència',
      },

      day: {
        shift: 'Torn', hours: 'Horari', duration: 'Durada',
        site: 'Centre', notes: 'Notes',
        absence: 'Absència', range: 'Dates', state: 'Estat', holiday: 'Festiu',
      },

      calendars: {
        title: 'Calendaris laborals',
        subtitle: 'Els dies que no es treballa, per centre i per any',
        new: 'Nou calendari', newTitle: 'Nou calendari laboral',
        name: 'Nom', namePlaceholder: 'Calendari Puigcerdà',
        year: 'Any', holidays: 'Festius',
        site: 'Centre', siteHint: 'Buit = tota l\'empresa', allSites: 'Tota l\'empresa',
        isDefault: 'És el calendari per defecte',
        isDefaultHint: 'El fa servir qui no en tingui cap altre assignat. Només un per any.',
        default: 'Per defecte',
        date: 'Data', holidayName: 'Què se celebra', unnamed: 'Sense nom',
        add: 'Afegeix', noHolidays: 'Encara no hi ha festius. Afegeix-los un a un mirant el BOE i '
          + 'el DOGC: canvien cada any i per això no els omplim nosaltres.',
        scopes: { national: 'Estatal', regional: 'Autonòmic', local: 'Local', company: 'D\'empresa' },
        preview: 'Festius de {name}',
        copyForward: 'Copia a {year}',
        copied: 'Calendari de {year} creat. Els festius s\'hi posen a mà: canvien cada any.',
        saved: 'Calendari desat',
      },

      team: {
        title: 'Calendari d\'equip',
        subtitle: 'La setmana d\'una colla, amb la cobertura a sota',
      },

      balances: {
        title: 'Saldos',
        subtitle: 'Quants dies té cada persona i quants n\'ha gastat',
        entitled: 'Assignats', consumed: 'Consumits',
        pending: 'Pendents', available: 'Disponibles',
        allocated: 'Assignació de l\'any', carriedOver: 'Vénen de l\'any anterior',
        carriedOverHint: 'Només si el conveni permet arrossegar-los.',
        accrualFrom: 'Amb dret des de', accrualTo: 'Fins a',
        accrualHint: 'Per a altes i baixes a mig any: l\'assignació es prorrateja per dies.',
        allocate: 'Assignar dies', allocateTitle: 'Assignació anual',
        adjust: 'Ajustar', adjustTitle: 'Ajustar el saldo',
        currently: 'Ara mateix té {days} dies',
        days: 'Dies', daysPlaceholder: '+1 o -1',
        reason: 'Motiu', reasonHint: 'Obligatori. Ho llegirà algú que no hi era, d\'aquí a dos anys.',
        adjusted: 'Saldo ajustat',
        openFile: 'Obre l\'expedient',
        rollOver: 'Passar a l\'any següent', rollOverTitle: 'Passar els saldos a l\'any següent',
        rollOverLead: 'Es copien les assignacions de {from} a {to}. Els ajustos no es copien: '
          + 'eren d\'aquell any.',
        carryUnused: 'Arrossegar els dies que sobren',
        carryUnusedHint: 'Si el conveni no ho diu, deixa-ho desactivat: ningú arrossega dies per '
          + 'defecte, i regalar-los és més difícil de desfer que concedir-los després.',
        rolledOver: '{count} saldos creats per a {year}',
      },

      // ── La fitxa laboral ────────────────────────────────────────────────
      people: {
        title: 'Personal',
        subtitle: 'Les dades laborals que el quadrant necessita per dibuixar-se',
        person: 'Persona', team: 'Colla', site: 'Centre',
        code: 'Núm.', contract: 'Contracte', category: 'Categoria', manager: 'Responsable',
        total: 'Persones', withEnd: 'Amb baixa prevista', unassigned: 'Sense categoria',
        openEnded: 'Indefinit', noContract: 'Sense dades',
        hoursShort: '{hours} h/set',
        since: 'Des del {date}', until: 'Fins al {date}',
        endsIn: 'Acaba d\'aquí a {days} dies', ended: 'Acabat',
        allCategories: 'Totes les categories',
        edit: 'Editar la fitxa', schedule: 'Veure el seu quadrant',
        managed: 'Ho porta Expedient',
        managedHint: 'Amb el mòdul d\'expedient instal·lat, aquestes dades s\'editen allà i aquí '
          + 'només es llegeixen. Dos llocs escrivint la mateixa data d\'alta és com deixen de '
          + 'coincidir.',
      },

      person: {
        title: 'Fitxa laboral',
        editTitle: 'Dades laborals',
        code: 'Núm. d\'empleat',
        contractType: 'Tipus de contracte',
        contractFrom: 'Alta', contractTo: 'Baixa prevista',
        contractToHint: 'Buit = indefinit. Fora d\'aquestes dates no es planifica.',
        weeklyHours: 'Hores setmanals',
        weeklyHoursHint: 'És el que converteix una absència demanada per hores en fracció de dia.',
        workingWeek: 'La seva setmana laboral',
        workingWeekHint: 'Sense marcar res es fa servir la de l\'empresa. Només per a qui no tingui quadrant.',
        category: 'Categoria',
        categoryHint: 'Com se sap d\'un cop d\'ull qui pot cobrir un torn.',
        manager: 'Responsable',
        managerHint: 'A qui li arriben les seves sol·licituds d\'absència.',
        noManager: 'Ningú en concret',
        workCalendar: 'Calendari laboral',
        workCalendarHint: 'Buit = el calendari per defecte de l\'any.',
        defaultCalendar: 'El de per defecte',
        notes: 'Notes', saved: 'Fitxa desada',
      },




      absenceTypes: {
        newTitle: 'Nou tipus d\'absència',
        name: 'Nom', code: 'Codi',
        codeHint: 'No es pot canviar després.',
        codeLocked: 'No es canvia: les absències ja desades el porten.',
        colour: 'Color',
        needs: 'Què cal', costs: 'Què costa',
        allows: 'Com es pot demanar', affects: 'A què afecta',
        requiresApproval: 'Algú l\'ha d\'aprovar',
        requiresReason: 'Cal explicar el motiu',
        requiresDocument: 'Cal adjuntar un justificant',
        approvalDays: 'Termini per respondre (dies)',
        approvalDaysHint: 'Serveix per ordenar la safata: el que passa de termini puja a dalt.',
        consumesBalance: 'Descompta dies d\'un saldo',
        balance: 'Saldo',
        balanceConcept: 'De quin saldo',
        balanceConceptHint: 'Un codi. Dos tipus amb el mateix codi comparteixen bossa.',
        isPaid: 'És retribuïda',
        allowsHalfDay: 'Es pot demanar mig dia',
        allowsHours: 'Es pot demanar per hores',
        affectsPlanning: 'Treu la persona del quadrant',
        affectsPlanningHint: 'Desactiva-ho en formació: qui està en un curs està treballant, i '
          + 'marcar-ho com absent fa que l\'equip sembli curt sense ser-ho.',
        affectsAttendance: 'Fitxatges deixa d\'esperar el seu fitxatge',
        affectsAttendanceHint: 'És el que evita que unes vacances surtin com a falta de fitxatge. '
          + 'Desactiva-ho només a l\'absència injustificada: que no va venir és justament el que '
          + 'el registre de jornada ha de continuar dient.',
        active: 'Actiu', saved: 'Tipus desat',
        short: {
          approval: 'aprovació', document: 'justificant',
          works: 'segueix al quadrant', attendance: 'compta als fitxatges',
        },
      },

      config: {
        title: 'Configuració',
        subtitle: 'El que es decideix un cop: torns, plantilles, tipus i cobertura',
        showArchived: 'Veure arxivats',
        shifts: 'Torns', patterns: 'Plantilles',
        absenceTypes: 'Tipus d\'absència',
        coverage: 'Cobertura',
        new: {
          shifts: 'Nou torn', patterns: 'Nova plantilla',
          absenceTypes: 'Nou tipus',
          coverage: 'Nova cobertura',
        },
      },

      empty: {
        noShifts: 'Encara no hi ha torns configurats',
        noShiftsHint: 'Un torn és un tram del rellotge amb nom: «Matí, de 06:00 a 14:00». Sense '
          + 'almenys un no es pot planificar res.',
        createShift: 'Crea el primer torn',
        noPeople: 'No hi ha ningú al quadrant',
        noPeopleHint: 'Les persones surten dels usuaris de la plataforma. Comprova als ajustos '
          + 'quins rols apareixen aquí.',
        noPatterns: 'Encara no hi ha plantilles',
        noPatternsHint: 'Una plantilla és la forma d\'un quadrant sense dates ni persones. És el '
          + 'que converteix planificar un mes per a dotze persones en una pulsació.',
        noRequests: 'No hi ha sol·licituds pendents',
        noRequestsHint: 'Quan algú demani vacances o un permís, apareixerà aquí.',
        noAbsences: 'No hi ha absències en aquest període',
        noAbsencesHint: 'Prova d\'ampliar les dates o de treure algun filtre.',
        noAbsencesForPerson: 'Aquesta persona no té absències registrades',
        noShiftsForMe: 'No tens torns planificats',
        noShiftsForMeHint: 'Tan bon punt es publiqui el quadrant apareixeran aquí.',
        noShiftsForPerson: 'Sense torns en aquest període',
        noBalance: 'Encara no tens saldos assignats',
        noBalanceHint: 'Els assigna qui porti el personal, un cop l\'any.',
        noBalances: 'Ningú té saldo d\'aquest concepte',
        noBalancesHint: 'Assigna\'ls persona a persona, o passa els de l\'any anterior.',
        noCalendars: 'Encara no hi ha calendaris laborals',
        noCalendarsHint: 'Un calendari són els festius d\'un centre i un any. Sense ell, un festiu '
          + 'es planifica com un dia qualsevol.',
        noCoverage: 'No hi ha cobertures mínimes configurades',
        noCoverageHint: 'És el que fa que aprovar unes vacances ensenyi amb quanta gent es queda '
          + 'la colla, en comptes de dos botons i res més.',
        noTeam: 'Tria una colla',
        noTeamHint: 'Les colles vénen del mòdul d\'equips. Sense ell, configura la cobertura sobre '
          + 'un centre.',
      },

      notifications: {
        categories: {
          published: 'Quadrant publicat',
          requested: 'Sol·licituds d\'absència',
          decided: 'Resposta a la teva sol·licitud',
          coverage: 'Un torn per sota del mínim',
        },
        coverage: {
          title: 'Un torn es queda per sota del mínim',
          body: '{days} dia(es) sota el mínim. El {date}: {available} de {required}.',
        },
        published: {
          title: 'Ja hi ha el quadrant',
          body: 'S\'ha publicat la planificació del {from} al {to}.',
        },
        absence: {
          requested: {
            title: 'Sol·licitud de {type}',
            body: '{worker} demana del {from} al {to}.',
          },
          approved: { title: 'Aprovada', body: 'La teva {type} del {from} al {to} està aprovada.' },
          rejected: { title: 'Rebutjada', body: 'La teva {type} del {from} al {to} no s\'ha aprovat.' },
          cancelled: { title: 'Anul·lada', body: 'S\'ha anul·lat la teva {type} del {from} al {to}.' },
        },
        unplanned: {
          title: 'Buits la setmana que ve',
          body: '{count} persones no tenen res posat del {from} al {to}.',
        },
      },

      // Las tarjetas del asistente (backend/assistant.js): pedir, aprobar y
      // rechazar una ausencia.
      assistant: {
        request: {
          summary: 'Demanar {type} del {from} al {to}',
          action: 'Demanar una absència',
          after: 'Queda pendent que algú l’aprovi',
          outcome: 'Demanat: {type} del {from} al {to}',
        },
        field: {
          type: 'Tipus',
          from: 'Del',
          to: 'Al',
          reason: 'Motiu',
          who: 'Qui',
          days: 'Dies',
          notes: 'Nota',
        },
        approve: {
          summary: 'Aprovar {type} de {who} del {from} al {to}',
          action: 'Aprovar absència',
          outcome: 'Aprovada: {who}, del {from} al {to}',
          breach: 'S’aprova encara que l’equip quedi per sota del mínim',
        },
        reject: {
          summary: 'Rebutjar {type} de {who} del {from} al {to}',
          action: 'Rebutjar absència',
          outcome: 'Rebutjada: {who}, del {from} al {to}',
        },
        decide: {
          notFound: 'No és clar quina sol·licitud és: tria-la a Sol·licituds',
          where: 'Sol·licitud',
        },
      },

      dashboard: {
        mine: 'Els meus propers torns',
        mineHelp: 'Els dies que treballes a partir d\'avui, amb el seu horari. Només el publicat.',
        myBalance: 'Els meus dies',
        myBalanceHelp: 'El que et queda de cada bossa: vacances, assumptes propis, el que hi hagi.',
        workingToday: 'Treballant avui',
        workingTodayHelp: 'Quantes persones tenen torn avui i no estan de baixa ni de vacances.',
        absentToday: 'Absents avui',
        absentTodayHelp: 'Quantes absències aprovades cobreixen el dia d\'avui.',
        unplanned: 'Sense planificar avui',
        unplannedHelp: 'Persones de les quals avui no es diu res: ni torn, ni descans, ni absència.',
        pending: 'Sol·licituds pendents',
        pendingHelp: 'Quantes absències esperen que algú les aprovi o les rebutgi.',
        coverage: 'Colles sota mínims',
        coverageHelp: 'Quantes cobertures mínimes no es compleixen avui.',
        upcoming: 'Absències que arriben',
        upcomingHelp: 'El que està aprovat i comença els propers dies, perquè doni temps a cobrir-ho.',
      },
    },

    modules: { workforce: 'Plantilla' },
    assistant: {
      openers: {
        workforce: {
          'when-do-i-work': 'Quan treballo aquesta setmana?',
          'my-holidays': 'Quants dies de vacances em queden?',
          'who-is-available': 'Amb qui compto el dilluns que ve?',
          'pending-requests': 'Quines sol·licituds d’absència m’esperen?',
        },
      },
    },
    permissions: {
      workforce: {
        me: 'Veure el meu quadrant i demanar absències',
        read: 'Veure el quadrant i les absències de tothom',
        plan: 'Repartir torns i publicar el quadrant',
        approve: 'Aprovar i rebutjar absències',
        manage: 'Configurar torns, calendaris, tipus i saldos',
        people: 'Veure i editar la fitxa laboral',
      },
    },
    settings: {
      ns: {
        workforce: {
          title: 'Personal',
          subtitle: 'Com es planifica, com es compten les absències i qui surt al quadrant',
          groups: { planning: 'Quadrant', absences: 'Absències', people: 'Persones' },
          weekStartsOn: {
            label: 'El primer dia de la setmana',
            help: 'Decideix per on comença la graella i sobre quines setmanes rota una plantilla.',
          },
          publishBeforeVisible: {
            label: 'El quadrant no es veu fins a publicar-lo',
            help: 'Activat, es prepara el mes que ve sense que ningú ho vegi. Desactivat, cada '
              + 'canvi apareix al moment a «El meu calendari».',
          },
          minRestHours: {
            label: 'Hores de descans mínim entre dos torns',
            help: 'Dotze és el que demana l\'Estatut. És un avís, no una prohibició.',
          },
          maxConsecutiveDays: {
            label: 'Dies seguits de feina abans d\'avisar',
            help: 'També un avís. A zero, no es comprova.',
          },
          workingWeek: {
            label: 'Dies laborables de la setmana',
            help: '1 = dilluns. És a què es recorre per comptar una '
              + 'absència de qui no té quadrant.',
          },
          standardDayMinutes: {
            label: 'Minuts d\'una jornada estàndard',
            help: 'Converteix una absència demanada per hores en fracció de dia.',
          },
          selfRequestAbsence: {
            label: 'Cada persona pot demanar les seves absències',
            help: 'Desactivat, només les posa qui té permís per aprovar-les.',
          },
          approverRoles: {
            label: 'Rols que reben avís d\'una sol·licitud',
            help: 'A més del responsable de la persona, si en té.',
          },
          contractTypes: {
            label: 'Tipus de contracte d\'aquesta empresa',
            help: 'Són els que ofereix el selector de la fitxa laboral.',
          },
          categories: {
            label: 'Categories professionals',
            help: 'És com se sap d\'un cop d\'ull qui pot cobrir un torn.',
          },
          workerRoles: {
            label: 'Rols que apareixen al quadrant',
            help: 'D\'aquí surt la llista de persones en instal·lar.',
          },
          warnUnplannedWeek: {
            label: 'Avisar els divendres si la setmana que ve té buits',
            help: 'Un avís a qui planifica amb la llista de qui no té res posat.',
          },
        },
      },
    },
    audit: {
      actions: {
        shift: {
          created: 'Torn creat',
          updated: 'Torn modificat',
          archived: 'Torn arxivat',
          restored: 'Torn retornat al catàleg',
        },
        pattern: {
          created: 'Plantilla creada',
          updated: 'Plantilla modificada',
          archived: 'Plantilla arxivada',
          restored: 'Plantilla retornada al catàleg',
        },
        calendar: {
          created: 'Calendari laboral creat',
          updated: 'Calendari laboral modificat',
          archived: 'Calendari laboral arxivat',
          restored: 'Calendari laboral retornat al catàleg',
        },
        absence_type: {
          created: 'Tipus d\'absència creat',
          updated: 'Tipus d\'absència modificat',
          deactivated: 'Tipus d\'absència desactivat',
        },
        plan: {
          assigned: 'Torns assignats',
          pattern_applied: 'Plantilla aplicada',
          copied: 'Setmana copiada',
          cleared: 'Planificació buidada',
          published: 'Quadrant publicat',
          publish_notice_failed: 'Error en avisar de la publicació',
        },
        absence: {
          requested: 'Absència sol·licitada',
          created: 'Absència registrada',
          updated: 'Absència modificada',
          approved: 'Absència aprovada',
          rejected: 'Absència rebutjada',
          cancelled: 'Absència anul·lada',
        },
        balance: {
          allocated: 'Saldo assignat',
          adjusted: 'Saldo ajustat',
          rolled_over: 'Saldos passats a l\'any següent',
        },
        coverage: {
          set: 'Cobertura mínima configurada',
          removed: 'Cobertura mínima eliminada',
          overridden: 'Aprovat sota la cobertura mínima',
        },
        people: {
          record_updated: 'Dades laborals modificades',
        },
      },
    },
    errors: {
      fields: {
        MANAGED_BY_PERSONNEL: 'Aquesta fitxa la porta el mòdul d\'expedient. S\'edita allà.',
        INVALID_TIME: 'Escriu-ho com HH:MM',
        INVALID_DATE: 'Això no és una data',
        ENDS_BEFORE_IT_STARTS: 'No pot acabar abans de començar',
        BREAK_TOO_LONG: 'La pausa no pot durar tant com el torn',
        RANGE_TOO_LONG: 'El rang és massa llarg (màxim {max})',
        PARTIAL_NEEDS_ONE_DAY: 'Una absència parcial és d\'un sol dia',
        UNIT_NOT_ALLOWED: 'Aquest tipus d\'absència no es pot demanar així',
        NOTHING_TO_ASSIGN: 'No hi ha res per assignar',
        NOTHING_TO_COPY: 'No hi ha res planificat per copiar',
        OVERLAPS_SOURCE: 'Ha de ser posterior al que es copia',
        SHIFT_NOT_FOUND: 'Apunta a un torn que ja no existeix',
        TYPE_NOT_FOUND: 'Aquest tipus no està disponible',
        WRONG_YEAR: 'Aquesta data no és de {year}',
        TOO_MANY: 'Són massa (màxim {max})',
      },
    },
  },

  en: {
    workforce: {
      title: 'Workforce',
      search: { subtitle: 'Rota and file' },

      actions: {
        save: 'Save', cancel: 'Cancel', close: 'Close', edit: 'Edit',
        delete: 'Delete', archive: 'Archive', restore: 'Bring back',
      },
      state: { active: 'Active', inactive: 'Inactive' },
      status: {
        pending: 'Pending', approved: 'Approved', rejected: 'Rejected', cancelled: 'Cancelled',
      },
      units: { day: 'Whole day', half_day: 'Half a day', hour: 'By the hour' },
      cell: {
        work: 'Shift', rest: 'Off', absent: 'Away',
        requested: 'Requested', holiday: 'Bank holiday', free: 'Not planned',
      },
      concepts: { holiday: 'Holiday', personal: 'Personal days' },

      seed: {
        absenceTypes: {
          holiday: 'Holiday', personal: 'Personal days',
          paid_leave: 'Paid leave', unpaid_leave: 'Unpaid leave',
          sick: 'Sick leave', accident: 'Work accident',
          parental: 'Parental leave', training: 'Training',
          justified: 'Justified absence', unjustified: 'Unjustified absence',
          other: 'Other',
        },
      },

      conflicts: {
        absence: 'is on {typeName}',
        absence_pending: 'has asked for {typeName} and nobody has decided',
        holiday: 'is a bank holiday ({name})',
        rest: 'only {hours} h of rest since the shift on {otherDate}, and the minimum is {required}',
        consecutive: 'that would be {days} days in a row, and the maximum is {max}',
      },

      filters: {
        allTeams: 'All crews', allShifts: 'All shifts',
        allStatuses: 'All states', allTypes: 'All kinds',
        searchWorker: 'Search for a person…',
      },

      planning: {
        title: 'Planning',
        subtitle: 'Who is working, on what shift, and what has to be covered',
        week: 'Week', month: 'Month', today: 'Today',
        prev: 'Previous', next: 'Next', jumpTo: 'Jump to a date',
        searchPlaceholder: 'Find somebody…',
        gridLabel: 'Rota by person and day',
        selectAll: 'All', selectRow: 'Select the whole row',
        selectColumn: 'Select the whole day',
        draftLegend: 'Unpublished',
        publish: 'Publish',
        publishCount: 'Publish ({count})',
        published: 'All published',
        publishTitle: 'Publish the rota',
        publishQuestion: 'From now on {count} shifts between {from} and {to} appear in «My '
          + 'calendar». Everybody affected gets one notification.',
        publishDone: '{count} shifts published for {workers} people',
      },

      stats: {
        working: 'Working today', absent: 'Away', rest: 'Off',
        unplanned: 'Not planned', weekHours: 'Hours this week',
      },

      assign: {
        title: 'Assign',
        selected: '{count} squares',
        hint: 'Pick squares on the rota: click one, drag along a row, or use the day heading and '
          + 'the name to take a whole column or a whole row.',
        scope: '{people} people · {days} days',
        shift: 'Shift', pickShift: 'Pick a shift', preview: 'Would be',
        apply: 'Assign', clear: 'Empty',
        clearTitle: 'Empty the squares',
        clearQuestion: '{count} shifts are removed. Anything already published stays.',
        cleared: '{count} squares emptied',
        done: '{count} shifts assigned',
        copyWeek: 'Copy a week', applyPattern: 'Apply a pattern',
        warnings: '{count} warnings',
        andMore: 'and {count} more',
      },

      copy: {
        title: 'Copy a week',
        lead: 'Copies whatever is planned between {from} and {to}.',
        target: 'Paste from', targetHint: 'It has to start after what is being copied.',
        into: 'It will land between {from} and {to}',
        onlySelected: 'Only the {count} people selected',
        confirm: 'Copy',
        done: '{count} shifts copied ({replaced} replaced something)',
        conflicts: '{count} warnings worth reading',
      },

      pattern: {
        newTitle: 'New pattern',
        applyTitle: 'Apply a pattern',
        name: 'Name', namePlaceholder: 'Office hours, Production rotation A…',
        description: 'Description',
        cycleWeeks: 'How many weeks it repeats over',
        cycleHint: 'One week is a fixed timetable. Three, the morning / afternoon / night rotation.',
        oneWeek: 'Every week', weeks: '{count} weeks',
        weekN: 'Week {week}', everyWeek: 'Every',
        free: 'Off', filled: 'Days',
        gridHint: 'Leave the days with no shift blank. A blank day means «nobody has decided»; to '
          + 'say «they are off», use a rest shift.',
        active: 'Active',
        pattern: 'Pattern', pick: 'Pick a pattern',
        from: 'From', to: 'To',
        cycle: 'Rotates every {weeks} weeks',
        everybody: 'Everybody ({count})',
        stagger: 'Stagger',
        startsOnWeek: 'Starts on week {week}',
        preview: 'See what it would do',
        previewCount: '{count} shifts for {people} people',
        previewEmpty: 'The pattern puts nothing in that range',
        apply: 'Apply ({count})',
        applied: '{count} shifts written, {warnings} warnings',
        andMore: 'and {count} more',
        saved: 'Pattern saved',
        archiveTitle: 'Archive the pattern',
        archiveQuestion: 'It stops being offered. The rotas it produced stay exactly as they are.',
        restored: 'Pattern brought back',
        archived: 'Pattern archived',
      },

      shifts: {
        newTitle: 'New shift',
        name: 'Name', namePlaceholder: 'Morning, Afternoon, Night…',
        code: 'Code', codeHint: 'One or two letters: it is what fits in a square.',
        kind: 'Kind', kindHint: 'A rest shift means «off», which is not the same as an empty square.',
        kindWork: 'Worked', kindRest: 'Rest day',
        start: 'Starts', end: 'Ends', break: 'Break (min)',
        hours: 'Hours', duration: 'Length',
        crossesMidnight: 'Crosses midnight',
        overnight: 'overnight',
        colour: 'Colour', description: 'Notes',
        site: 'Site', siteHint: 'Empty = anywhere', allSites: 'All',
        active: 'Active',
        saved: 'Shift saved',
        archiveTitle: 'Archive the shift',
        archiveQuestion: 'It stops being offered when planning. What is already on the rota stays.',
        restored: 'Shift brought back',
        archived: 'Shift archived',
      },

      absences: {
        title: 'Absences',
        subtitle: 'Who is away, when, and who approved it',
        new: 'Record an absence',
        overrode: 'Approved leaving the crew below its minimum',
      },

      request: {
        title: 'Ask for time off',
        type: 'Kind', pickType: 'Pick a kind',
        from: 'From', to: 'To',
        unit: 'How much', fromTime: 'From', toTime: 'Until',
        reason: 'Reason', notes: 'Comment',
        calendarDays: 'Calendar days', workingDays: 'Working days',
        holidays: 'Bank holidays', shiftsAffected: 'Shifts affected',
        chargeable: 'Comes off', days: 'days',
        balanceBefore: 'Allowance',
        notEnough: 'You are {short} days short. You can still ask; whoever approves decides.',
        overlaps: 'You already have {type} between {from} and {to}. Cancel that one first.',
        noApproval: 'This kind needs no approval: it is recorded straight away.',
        send: 'Send the request',
        sent: 'Request sent. Somebody still has to approve it.',
        recorded: 'Absence recorded',
      },

      requests: {
        title: 'Requests',
        subtitle: 'What is waiting on an answer',
        waiting: 'Waiting', late: 'Past the target',
        worker: 'Person',
        overdue: 'Overdue', imminent: 'Starts soon',
        requestedBy: 'Asked by', themselves: 'Themselves',
        fileFor: 'Record for somebody',
      },

      approve: {
        balance: 'Allowance',
        balanceOf: 'of {concept}',
        overdrawn: 'They would be {days} days over their allowance.',
        noTeam: 'No crew',
        noRule: 'This crew has no minimum coverage configured.',
        checking: 'Checking the coverage…',
        fine: 'Coverage holds on every day.',
        breach: 'Approving this leaves the crew below its minimum on {days} days.',
        alsoAway: 'Also away: {names}',
        notes: 'Comment', notesHint: 'Whoever asked will read it.',
        acceptBreach: 'I have read the coverage and approve it anyway',
        acceptBreachHint: 'It is recorded against your name and announced on the bus, so whoever '
          + 'plans the week knows.',
        blocked: 'This would leave the crew below its minimum. Read it before deciding.',
        approve: 'Approve', reject: 'Reject',
        approvedDone: 'Absence approved', rejectedDone: 'Request rejected',
        selfApproval: 'Nobody decides their own absences: somebody else with permission to approve has to.',
        alreadyDecided: 'Somebody else has just decided this absence. Reload to see how it stands.',
        alreadyStarted: 'This absence has already started and can no longer be withdrawn. Ask whoever approves.',
      },

      coverage: {
        title: 'Coverage',
        short: 'Days below the minimum',
        newTitle: 'New minimum coverage',
        status: {
          ok: 'Coverage is fine', at_minimum: 'Exactly the minimum',
          below: 'Not enough cover', no_rule: 'No rule',
        },
        away: 'Away: {names}',
        scope: 'What it is about', scopeHint: 'A crew if you run the teams module; otherwise a site.',
        scopeTeam: 'Crew', scopeSite: 'Site',
        team: 'Crew', site: 'Site', target: 'About',
        pickTarget: 'Pick one',
        shift: 'Shift', shiftHint: 'Empty = counts everybody, whatever shift they are on.',
        anyShift: 'Any',
        minimum: 'Minimum',
        policy: 'What happens below the minimum',
        policies: { warn: 'Only warn', confirm: 'Ask for confirmation', block: 'Refuse' },
        policyHint: {
          warn: 'The number is shown and it is approved anyway. For a minimum that is a guide.',
          confirm: 'A box has to be ticked saying it was read. It is recorded against whoever '
            + 'approves, and it is the option that actually changes behaviour.',
          block: 'It cannot be approved. Only where the minimum is a legal or safety limit.',
        },
        weekdays: 'Days it applies on', weekdaysHint: 'Nothing ticked means every day.',
        saved: 'Coverage saved',
        removeTitle: 'Remove the minimum coverage',
        removeQuestion: 'The crew stops being checked when an absence is approved.',
        removed: 'Coverage removed',
      },

      my: {
        title: 'My calendar',
        subtitle: 'When I work and how many days I have left',
        today: 'Today', todayShort: 'Today', tomorrow: 'Tomorrow',
        next: 'Next shifts',
        balance: 'My days',
        pendingDays: '{count} waiting on a decision',
        request: 'Ask for time off',
      },

      day: {
        shift: 'Shift', hours: 'Hours', duration: 'Length',
        site: 'Site', notes: 'Notes',
        absence: 'Absence', range: 'Dates', state: 'State', holiday: 'Bank holiday',
      },

      calendars: {
        title: 'Working calendars',
        subtitle: 'The days nobody works, by site and by year',
        new: 'New calendar', newTitle: 'New working calendar',
        name: 'Name', namePlaceholder: 'Puigcerdà calendar',
        year: 'Year', holidays: 'Bank holidays',
        site: 'Site', siteHint: 'Empty = the whole company', allSites: 'The whole company',
        isDefault: 'This is the default calendar',
        isDefaultHint: 'Used by anybody with no calendar of their own. One per year.',
        default: 'Default',
        date: 'Date', holidayName: 'What it is', unnamed: 'Unnamed',
        add: 'Add', noHolidays: 'No bank holidays yet. Add them one by one from the official '
          + 'bulletins: they move every year, which is why we do not fill them in for you.',
        scopes: { national: 'National', regional: 'Regional', local: 'Local', company: 'Company' },
        preview: 'Bank holidays of {name}',
        copyForward: 'Copy to {year}',
        copied: '{year} calendar created. The days go in by hand: they move every year.',
        saved: 'Calendar saved',
      },

      team: {
        title: 'Team calendar',
        subtitle: 'One crew\'s week, with the coverage underneath',
      },

      balances: {
        title: 'Allowances',
        subtitle: 'How many days each person has and how many they have used',
        entitled: 'Allocated', consumed: 'Used',
        pending: 'Requested', available: 'Left',
        allocated: 'This year\'s allocation', carriedOver: 'Carried from last year',
        carriedOverHint: 'Only if the agreement allows carrying days over.',
        accrualFrom: 'Entitled from', accrualTo: 'Until',
        accrualHint: 'For mid-year starters and leavers: the allocation is prorated by days.',
        allocate: 'Allocate days', allocateTitle: 'Yearly allocation',
        adjust: 'Adjust', adjustTitle: 'Adjust the allowance',
        currently: 'Right now they have {days} days',
        days: 'Days', daysPlaceholder: '+1 or -1',
        reason: 'Reason', reasonHint: 'Required. Somebody who was not there will read it in two years.',
        adjusted: 'Allowance adjusted',
        openFile: 'Open the file',
        rollOver: 'Roll over to next year', rollOverTitle: 'Roll the allowances over',
        rollOverLead: 'The allocations of {from} are copied into {to}. Adjustments are not: they '
          + 'belonged to that year.',
        carryUnused: 'Carry the unused days over',
        carryUnusedHint: 'If the agreement does not say so, leave it off: nobody carries days by '
          + 'default, and giving them away is harder to undo than granting them later.',
        rolledOver: '{count} allowances created for {year}',
      },

      // ── The labour card ─────────────────────────────────────────────────
      people: {
        title: 'People',
        subtitle: 'The employment facts a rota cannot be drawn without',
        person: 'Person', team: 'Crew', site: 'Site',
        code: 'No.', contract: 'Contract', category: 'Category', manager: 'Manager',
        total: 'People', withEnd: 'With an end date', unassigned: 'No category',
        openEnded: 'Open-ended', noContract: 'Nothing on file',
        hoursShort: '{hours} h/week',
        since: 'Since {date}', until: 'Until {date}',
        endsIn: 'Ends in {days} days', ended: 'Ended',
        allCategories: 'Every category',
        edit: 'Edit the card', schedule: 'See their rota',
        managed: 'Kept in the employee file',
        managedHint: 'With the employee-file module installed, these are edited there and only '
          + 'read here. Two screens writing the same hire date is how the two stop agreeing.',
      },

      person: {
        title: 'Labour card',
        editTitle: 'Employment details',
        code: 'Staff number',
        contractType: 'Kind of contract',
        contractFrom: 'Started', contractTo: 'Ends',
        contractToHint: 'Empty = open-ended. Nobody is planned outside these dates.',
        weeklyHours: 'Weekly hours',
        weeklyHoursHint: 'This is what turns time off asked for in hours into a fraction of a day.',
        workingWeek: 'Their working week',
        workingWeekHint: 'Nothing ticked uses the company\'s. Only for people with no rota.',
        category: 'Category',
        categoryHint: 'How anybody tells at a glance who can cover a shift.',
        manager: 'Manager',
        managerHint: 'Who their time-off requests go to.',
        noManager: 'Nobody in particular',
        workCalendar: 'Working calendar',
        workCalendarHint: 'Empty = the year\'s default calendar.',
        defaultCalendar: 'The default one',
        notes: 'Notes', saved: 'Card saved',
      },




      absenceTypes: {
        newTitle: 'New kind of absence',
        name: 'Name', code: 'Code',
        codeHint: 'It cannot be changed afterwards.',
        codeLocked: 'Not editable: absences already filed carry it.',
        colour: 'Colour',
        needs: 'What it needs', costs: 'What it costs',
        allows: 'How it can be asked for', affects: 'What it affects',
        requiresApproval: 'Somebody has to approve it',
        requiresReason: 'A reason has to be given',
        requiresDocument: 'Paperwork has to be attached',
        approvalDays: 'Target to answer (days)',
        approvalDaysHint: 'Sorts the tray: anything past its target goes to the top.',
        consumesBalance: 'Comes off an allowance',
        balance: 'Allowance',
        balanceConcept: 'Which allowance',
        balanceConceptHint: 'A code. Two kinds with the same code share a pot.',
        isPaid: 'It is paid',
        allowsHalfDay: 'Half a day can be asked for',
        allowsHours: 'It can be asked for by the hour',
        affectsPlanning: 'Takes the person off the rota',
        affectsPlanningHint: 'Turn it off for training: somebody on a course is at work, and '
          + 'marking them away makes the crew look short when it is not.',
        affectsAttendance: 'Attendance stops expecting them to clock in',
        affectsAttendanceHint: 'This is what stops a holiday reading as a missing clock-in. Only '
          + 'turn it off for an unjustified absence: that they did not turn up is exactly what '
          + 'the working-time record should keep saying.',
        active: 'Active', saved: 'Type saved',
        short: {
          approval: 'approval', document: 'paperwork',
          works: 'stays on the rota', attendance: 'counts in attendance',
        },
      },

      config: {
        title: 'Settings',
        subtitle: 'What is decided once: shifts, patterns, kinds and coverage',
        showArchived: 'Show archived',
        shifts: 'Shifts', patterns: 'Patterns',
        absenceTypes: 'Absence kinds',
        coverage: 'Coverage',
        new: {
          shifts: 'New shift', patterns: 'New pattern',
          absenceTypes: 'New kind',
          coverage: 'New coverage',
        },
      },

      empty: {
        noShifts: 'No shifts configured yet',
        noShiftsHint: 'A shift is a named stretch of the clock: «Morning, 06:00 to 14:00». Without '
          + 'at least one there is nothing to plan with.',
        createShift: 'Create the first shift',
        noPeople: 'Nobody is on the rota',
        noPeopleHint: 'People come from the platform\'s users. Check in the settings which roles '
          + 'appear here.',
        noPatterns: 'No patterns yet',
        noPatternsHint: 'A pattern is the shape of a rota with no dates and no people. It is what '
          + 'turns planning a month for twelve people into one press.',
        noRequests: 'Nothing is waiting',
        noRequestsHint: 'When somebody asks for time off it will appear here.',
        noAbsences: 'No absences in this period',
        noAbsencesHint: 'Try widening the dates or dropping a filter.',
        noAbsencesForPerson: 'Nothing on file for this person',
        noShiftsForMe: 'You have no shifts planned',
        noShiftsForMeHint: 'As soon as the rota is published they will show up here.',
        noShiftsForPerson: 'No shifts in this period',
        noBalance: 'You have no allowances yet',
        noBalanceHint: 'Whoever runs the staff sets them, once a year.',
        noBalances: 'Nobody has an allowance of this kind',
        noBalancesHint: 'Set them person by person, or roll last year\'s over.',
        noCalendars: 'No working calendars yet',
        noCalendarsHint: 'A calendar is the bank holidays of a site and a year. Without one, a '
          + 'bank holiday is planned like any other day.',
        noCoverage: 'No minimum coverage configured',
        noCoverageHint: 'It is what makes approving a holiday show how many people the crew is '
          + 'left with, instead of two buttons and nothing else.',
        noTeam: 'Pick a crew',
        noTeamHint: 'Crews come from the teams module. Without it, set the coverage on a site.',
      },

      notifications: {
        categories: {
          published: 'Rota published',
          requested: 'Absence requests',
          decided: 'An answer to your request',
          coverage: 'A shift left below its minimum',
        },
        coverage: {
          title: 'A shift is left below its minimum',
          body: '{days} day(s) under the minimum. On {date}: {available} of {required}.',
        },
        published: {
          title: 'The rota is out',
          body: 'The rota between {from} and {to} has been published.',
        },
        absence: {
          requested: {
            title: '{type} requested',
            body: '{worker} is asking for {from} to {to}.',
          },
          approved: { title: 'Approved', body: 'Your {type} from {from} to {to} is approved.' },
          rejected: { title: 'Rejected', body: 'Your {type} from {from} to {to} was not approved.' },
          cancelled: { title: 'Cancelled', body: 'Your {type} from {from} to {to} has been cancelled.' },
        },
        unplanned: {
          title: 'Gaps next week',
          body: '{count} people have nothing planned between {from} and {to}.',
        },
      },

      // Las tarjetas del asistente (backend/assistant.js): pedir, aprobar y
      // rechazar una ausencia.
      assistant: {
        request: {
          summary: 'Ask for {type} from {from} to {to}',
          action: 'Ask for time off',
          after: 'It waits for somebody to approve it',
          outcome: 'Requested: {type} from {from} to {to}',
        },
        field: {
          type: 'Kind',
          from: 'From',
          to: 'To',
          reason: 'Reason',
          who: 'Who',
          days: 'Days',
          notes: 'Note',
        },
        approve: {
          summary: 'Approve {who}’s {type} from {from} to {to}',
          action: 'Approve absence',
          outcome: 'Approved: {who}, {from} to {to}',
          breach: 'Approved although the crew stays below its minimum',
        },
        reject: {
          summary: 'Reject {who}’s {type} from {from} to {to}',
          action: 'Reject absence',
          outcome: 'Rejected: {who}, {from} to {to}',
        },
        decide: {
          notFound: 'Which request is not clear: pick it on the Requests screen',
          where: 'Request',
        },
      },

      dashboard: {
        mine: 'My next shifts',
        mineHelp: 'The days you work from today, with their hours. Published rota only.',
        myBalance: 'My days',
        myBalanceHelp: 'What is left of each allowance: holiday, personal days, whatever there is.',
        workingToday: 'Working today',
        workingTodayHelp: 'How many people are down for a shift today and are not away.',
        absentToday: 'Away today',
        absentTodayHelp: 'How many approved absences cover today.',
        unplanned: 'Not planned today',
        unplannedHelp: 'People nothing is said about today: no shift, no rest day, no absence.',
        pending: 'Requests waiting',
        pendingHelp: 'How many absences are waiting for somebody to approve or reject them.',
        coverage: 'Crews below the minimum',
        coverageHelp: 'How many minimum coverage rules are not met today.',
        upcoming: 'Absences coming up',
        upcomingHelp: 'What is approved and starts in the next few days, while there is still time.',
      },
    },

    modules: { workforce: 'Workforce' },
    assistant: {
      openers: {
        workforce: {
          'when-do-i-work': 'When do I work this week?',
          'my-holidays': 'How many holidays have I got left?',
          'who-is-available': 'Who can I count on next Monday?',
          'pending-requests': 'Which absence requests are waiting for me?',
        },
      },
    },
    permissions: {
      workforce: {
        me: 'See my own rota and ask for time off',
        read: 'See everybody\'s rota and absences',
        plan: 'Assign shifts and publish the rota',
        approve: 'Approve and reject absences',
        manage: 'Configure shifts, calendars, kinds and allowances',
        people: 'Read and edit the labour card',
      },
    },
    settings: {
      ns: {
        workforce: {
          title: 'People',
          subtitle: 'How the rota is planned, how absences are counted and who appears on it',
          groups: { planning: 'Rota', absences: 'Absences', people: 'People' },
          weekStartsOn: {
            label: 'The first day of the week',
            help: 'Decides where the grid starts and which weeks a pattern rotates over.',
          },
          publishBeforeVisible: {
            label: 'The rota is not visible until it is published',
            help: 'On, next month is prepared without anybody seeing it. Off, every change shows '
              + 'up in «My calendar» straight away.',
          },
          minRestHours: {
            label: 'Minimum hours of rest between two shifts',
            help: 'Twelve is what Spanish law asks for. It is a warning, not a refusal.',
          },
          maxConsecutiveDays: {
            label: 'Days in a row before warning',
            help: 'Also a warning. Zero switches the check off.',
          },
          workingWeek: {
            label: 'Working days of the week',
            help: '1 = Monday. It is the fallback for counting an '
              + 'absence for somebody with no rota.',
          },
          standardDayMinutes: {
            label: 'Minutes in a standard working day',
            help: 'Turns an absence asked for in hours into a fraction of a day.',
          },
          selfRequestAbsence: {
            label: 'Everybody can ask for their own time off',
            help: 'Off, only whoever can approve absences may file one.',
          },
          approverRoles: {
            label: 'Roles notified about a request',
            help: 'As well as the person\'s own manager, if they have one.',
          },
          contractTypes: {
            label: 'Kinds of contract this company uses',
            help: 'They are what the file suggests.',
          },
          categories: {
            label: 'Professional categories',
            help: 'How anybody tells at a glance who can cover a shift.',
          },
          workerRoles: {
            label: 'Roles that appear on the rota',
            help: 'Where the list of people comes from on install.',
          },
          warnUnplannedWeek: {
            label: 'Warn on Fridays if next week has gaps',
            help: 'One notice to whoever plans, listing who has nothing down.',
          },
        },
      },
    },
    audit: {
      actions: {
        shift: {
          created: 'Shift created',
          updated: 'Shift changed',
          archived: 'Shift archived',
          restored: 'Shift brought back',
        },
        pattern: {
          created: 'Pattern created',
          updated: 'Pattern changed',
          archived: 'Pattern archived',
          restored: 'Pattern brought back',
        },
        calendar: {
          created: 'Working calendar created',
          updated: 'Working calendar changed',
          archived: 'Working calendar archived',
          restored: 'Work calendar brought back',
        },
        absence_type: {
          created: 'Absence kind created',
          updated: 'Absence kind changed',
          deactivated: 'Absence kind switched off',
        },
        plan: {
          assigned: 'Shifts assigned',
          pattern_applied: 'Pattern applied',
          copied: 'Week copied',
          cleared: 'Rota emptied',
          published: 'Rota published',
          publish_notice_failed: 'Could not announce the publication',
        },
        absence: {
          requested: 'Time off requested',
          created: 'Absence recorded',
          updated: 'Absence changed',
          approved: 'Absence approved',
          rejected: 'Absence rejected',
          cancelled: 'Absence cancelled',
        },
        balance: {
          allocated: 'Allowance allocated',
          adjusted: 'Allowance adjusted',
          rolled_over: 'Allowances rolled over',
        },
        coverage: {
          set: 'Minimum coverage configured',
          removed: 'Minimum coverage removed',
          overridden: 'Approved below the minimum coverage',
        },
        people: {
          record_updated: 'Employment details changed',
        },
      },
    },
    errors: {
      fields: {
        MANAGED_BY_PERSONNEL: 'This card is kept in the employee-file module. Edit it there.',
        INVALID_TIME: 'Write it as HH:MM',
        INVALID_DATE: 'That is not a date',
        ENDS_BEFORE_IT_STARTS: 'It cannot end before it starts',
        BREAK_TOO_LONG: 'The break cannot be as long as the shift',
        RANGE_TOO_LONG: 'That range is too long (at most {max})',
        PARTIAL_NEEDS_ONE_DAY: 'A part-day absence covers one day',
        UNIT_NOT_ALLOWED: 'This kind of absence cannot be asked for that way',
        NOTHING_TO_ASSIGN: 'There is nothing to assign',
        NOTHING_TO_COPY: 'There is nothing planned to copy',
        OVERLAPS_SOURCE: 'It has to start after what is being copied',
        SHIFT_NOT_FOUND: 'It points at a shift that no longer exists',
        TYPE_NOT_FOUND: 'That kind is not available',
        WRONG_YEAR: 'That date is not in {year}',
        TOO_MANY: 'That is too many (at most {max})',
      },
    },
  },

  fr: {
    workforce: {
      title: 'Effectifs',
      search: { subtitle: 'Planning et dossier' },

      actions: {
        save: 'Enregistrer', cancel: 'Annuler', close: 'Fermer', edit: 'Modifier',
        delete: 'Supprimer', archive: 'Archiver', restore: 'Remettre au catalogue',
      },
      state: { active: 'Actif', inactive: 'Inactif' },
      status: {
        pending: 'En attente', approved: 'Approuvée', rejected: 'Refusée', cancelled: 'Annulée',
      },
      units: { day: 'Journée entière', half_day: 'Demi-journée', hour: 'En heures' },
      cell: {
        work: 'Poste', rest: 'Repos', absent: 'Absent',
        requested: 'Demandé', holiday: 'Férié', free: 'Non planifié',
      },
      concepts: { holiday: 'Congés', personal: 'Jours personnels' },

      seed: {
        absenceTypes: {
          holiday: 'Congés', personal: 'Jours personnels',
          paid_leave: 'Congé rémunéré', unpaid_leave: 'Congé sans solde',
          sick: 'Arrêt maladie', accident: 'Accident du travail',
          parental: 'Congé parental', training: 'Formation',
          justified: 'Absence justifiée', unjustified: 'Absence injustifiée',
          other: 'Autres',
        },
      },

      conflicts: {
        absence: 'est en {typeName}',
        absence_pending: 'a demandé {typeName} et personne n\'a tranché',
        holiday: 'est un jour férié ({name})',
        rest: 'seulement {hours} h de repos depuis le poste du {otherDate}, et le minimum est {required}',
        consecutive: 'cela ferait {days} jours d\'affilée, et le maximum est {max}',
      },

      filters: {
        allTeams: 'Toutes les équipes', allShifts: 'Tous les postes',
        allStatuses: 'Tous les états', allTypes: 'Tous les types',
        searchWorker: 'Chercher une personne…',
      },

      planning: {
        title: 'Planification',
        subtitle: 'Qui travaille, sur quel poste, et ce qu\'il faut couvrir',
        week: 'Semaine', month: 'Mois', today: 'Aujourd\'hui',
        prev: 'Précédent', next: 'Suivant', jumpTo: 'Aller à une date',
        searchPlaceholder: 'Chercher quelqu\'un…',
        gridLabel: 'Planning par personne et par jour',
        selectAll: 'Tout', selectRow: 'Sélectionner toute la ligne',
        selectColumn: 'Sélectionner toute la journée',
        draftLegend: 'Non publié',
        publish: 'Publier',
        publishCount: 'Publier ({count})',
        published: 'Tout est publié',
        publishTitle: 'Publier la planification',
        publishQuestion: 'Désormais {count} postes du {from} au {to} apparaîtront dans «Mon '
          + 'calendrier». Chaque personne concernée reçoit un avis.',
        publishDone: '{count} postes publiés pour {workers} personnes',
      },

      stats: {
        working: 'Au travail aujourd\'hui', absent: 'Absents', rest: 'En repos',
        unplanned: 'Non planifiés', weekHours: 'Heures de la semaine',
      },

      assign: {
        title: 'Affecter',
        selected: '{count} cases',
        hint: 'Sélectionne des cases sur le planning : clique-en une, glisse le long d\'une ligne, '
          + 'ou utilise l\'en-tête du jour et le nom pour prendre une colonne ou une ligne entière.',
        scope: '{people} personnes · {days} jours',
        shift: 'Poste', pickShift: 'Choisis un poste', preview: 'Cela donnerait',
        apply: 'Affecter', clear: 'Vider',
        clearTitle: 'Vider les cases',
        clearQuestion: '{count} postes sont retirés. Ce qui est déjà publié reste.',
        cleared: '{count} cases vidées',
        done: '{count} postes affectés',
        copyWeek: 'Copier une semaine', applyPattern: 'Appliquer un modèle',
        warnings: '{count} avertissements',
        andMore: 'et {count} de plus',
      },

      copy: {
        title: 'Copier une semaine',
        lead: 'Copie ce qui est planifié du {from} au {to}.',
        target: 'Coller à partir du', targetHint: 'Doit commencer après ce qui est copié.',
        into: 'Cela ira du {from} au {to}',
        onlySelected: 'Seulement les {count} personnes sélectionnées',
        confirm: 'Copier',
        done: '{count} postes copiés ({replaced} en remplaçaient d\'autres)',
        conflicts: '{count} avertissements à regarder',
      },

      pattern: {
        newTitle: 'Nouveau modèle',
        applyTitle: 'Appliquer un modèle',
        name: 'Nom', namePlaceholder: 'Horaires de bureau, Rotation production A…',
        description: 'Description',
        cycleWeeks: 'Sur combien de semaines il se répète',
        cycleHint: 'Une semaine, c\'est un horaire fixe. Trois, la rotation matin / après-midi / nuit.',
        oneWeek: 'Chaque semaine', weeks: '{count} semaines',
        weekN: 'Semaine {week}', everyWeek: 'Toutes',
        free: 'Repos', filled: 'Jours',
        gridHint: 'Laisse vides les jours sans poste. Une case vide veut dire «rien de décidé» ; '
          + 'pour dire «en repos», utilise un poste de type repos.',
        active: 'Actif',
        pattern: 'Modèle', pick: 'Choisis un modèle',
        from: 'Du', to: 'Au',
        cycle: 'Tourne toutes les {weeks} semaines',
        everybody: 'Tout le monde ({count})',
        stagger: 'Décaler',
        startsOnWeek: 'Commence à la semaine {week}',
        preview: 'Voir ce que cela ferait',
        previewCount: '{count} postes pour {people} personnes',
        previewEmpty: 'Le modèle ne pose rien sur cette période',
        apply: 'Appliquer ({count})',
        applied: '{count} postes créés, {warnings} avertissements',
        andMore: 'et {count} de plus',
        saved: 'Modèle enregistré',
        archiveTitle: 'Archiver le modèle',
        archiveQuestion: 'Il n\'est plus proposé. Les plannings qui en sont sortis restent tels quels.',
        restored: 'Modèle remis au catalogue',
        archived: 'Modèle archivé',
      },

      shifts: {
        newTitle: 'Nouveau poste',
        name: 'Nom', namePlaceholder: 'Matin, Après-midi, Nuit…',
        code: 'Code', codeHint: 'Une ou deux lettres : c\'est ce qui tient dans une case.',
        kind: 'Type', kindHint: 'Un poste de repos veut dire «ne travaille pas», ce qui n\'est pas une case vide.',
        kindWork: 'Travaillé', kindRest: 'Repos',
        start: 'Début', end: 'Fin', break: 'Pause (min)',
        hours: 'Horaire', duration: 'Durée',
        crossesMidnight: 'Passe minuit',
        overnight: 'de nuit',
        colour: 'Couleur', description: 'Notes',
        site: 'Site', siteHint: 'Vide = n\'importe lequel', allSites: 'Tous',
        active: 'Actif',
        saved: 'Poste enregistré',
        archiveTitle: 'Archiver le poste',
        archiveQuestion: 'Il n\'est plus proposé à la planification. Ce qui est déjà au planning reste.',
        restored: 'Poste remis au catalogue',
        archived: 'Poste archivé',
      },

      absences: {
        title: 'Absences',
        subtitle: 'Qui manque, quand, et qui l\'a approuvé',
        new: 'Enregistrer une absence',
        overrode: 'Approuvée en laissant l\'équipe sous le minimum',
      },

      request: {
        title: 'Demander une absence',
        type: 'Type', pickType: 'Choisis le type',
        from: 'Du', to: 'Au',
        unit: 'Combien', fromTime: 'De', toTime: 'À',
        reason: 'Motif', notes: 'Commentaire',
        calendarDays: 'Jours calendaires', workingDays: 'Jours ouvrés',
        holidays: 'Jours fériés', shiftsAffected: 'Postes concernés',
        chargeable: 'Décomptés', days: 'jours',
        balanceBefore: 'Solde',
        notEnough: 'Il te manque {short} jours. Tu peux demander quand même ; c\'est celui qui '
          + 'approuve qui décide.',
        overlaps: 'Tu as déjà {type} du {from} au {to}. Annule celle-là d\'abord.',
        noApproval: 'Ce type n\'a pas besoin d\'approbation : il est enregistré directement.',
        send: 'Envoyer la demande',
        sent: 'Demande envoyée. Elle attend une approbation.',
        recorded: 'Absence enregistrée',
      },

      requests: {
        title: 'Demandes',
        subtitle: 'Ce qui attend une réponse',
        waiting: 'En attente', late: 'Hors délai',
        worker: 'Personne',
        overdue: 'Hors délai', imminent: 'Commence bientôt',
        requestedBy: 'Demandé par', themselves: 'La personne',
        fileFor: 'Enregistrer pour quelqu\'un',
      },

      approve: {
        balance: 'Solde',
        balanceOf: 'de {concept}',
        overdrawn: 'Elle serait à {days} jours au-delà de son solde.',
        noTeam: 'Sans équipe',
        noRule: 'Cette équipe n\'a pas de couverture minimale configurée.',
        checking: 'Vérification de la couverture…',
        fine: 'La couverture tient tous les jours.',
        breach: 'Approuver laisse l\'équipe sous le minimum {days} jours.',
        alsoAway: 'Également absents : {names}',
        notes: 'Commentaire', notesHint: 'La personne qui a demandé le verra.',
        acceptBreach: 'J\'ai lu la couverture et j\'approuve quand même',
        acceptBreachHint: 'C\'est enregistré à ton nom et annoncé sur le bus, pour que celui qui '
          + 'planifie la semaine le sache.',
        blocked: 'Cela laisserait l\'équipe sous le minimum. Regarde avant de décider.',
        approve: 'Approuver', reject: 'Refuser',
        approvedDone: 'Absence approuvée', rejectedDone: 'Demande refusée',
        selfApproval: 'Personne ne décide de ses propres absences : une autre personne autorisée à approuver doit le faire.',
        alreadyDecided: 'Une autre personne vient de décider de cette absence. Rechargez pour voir où elle en est.',
        alreadyStarted: 'Cette absence a déjà commencé et ne peut plus être retirée. Demandez-le à la personne qui approuve.',
      },

      coverage: {
        title: 'Couverture',
        short: 'Jours sous le minimum',
        newTitle: 'Nouvelle couverture minimale',
        status: {
          ok: 'Couverture correcte', at_minimum: 'Juste le minimum',
          below: 'Couverture insuffisante', no_rule: 'Aucune règle',
        },
        away: 'Absents : {names}',
        scope: 'Sur quoi', scopeHint: 'Une équipe si tu as le module équipes ; sinon un site.',
        scopeTeam: 'Équipe', scopeSite: 'Site',
        team: 'Équipe', site: 'Site', target: 'Sur',
        pickTarget: 'Choisis',
        shift: 'Poste', shiftHint: 'Vide = compte tout le monde, quel que soit son poste.',
        anyShift: 'N\'importe lequel',
        minimum: 'Minimum',
        policy: 'Ce qui se passe sous le minimum',
        policies: { warn: 'Avertir seulement', confirm: 'Demander confirmation', block: 'Bloquer' },
        policyHint: {
          warn: 'Le chiffre s\'affiche et on approuve quand même. Pour un minimum indicatif.',
          confirm: 'Il faut cocher une case disant qu\'on a lu. C\'est enregistré au nom de celui '
            + 'qui approuve, et c\'est l\'option qui change vraiment les comportements.',
          block: 'Impossible d\'approuver. Seulement là où le minimum est légal ou de sécurité.',
        },
        weekdays: 'Jours où elle s\'applique', weekdaysHint: 'Rien de coché = tous les jours.',
        saved: 'Couverture enregistrée',
        removeTitle: 'Retirer la couverture minimale',
        removeQuestion: 'L\'équipe n\'est plus vérifiée lors d\'une approbation.',
        removed: 'Couverture supprimée',
      },

      my: {
        title: 'Mon calendrier',
        subtitle: 'Quand je travaille et combien de jours il me reste',
        today: 'Aujourd\'hui', todayShort: 'Aujourd\'hui', tomorrow: 'Demain',
        next: 'Prochains postes',
        balance: 'Mes jours',
        pendingDays: '{count} en attente d\'approbation',
        request: 'Demander une absence',
      },

      day: {
        shift: 'Poste', hours: 'Horaire', duration: 'Durée',
        site: 'Site', notes: 'Notes',
        absence: 'Absence', range: 'Dates', state: 'État', holiday: 'Jour férié',
      },

      calendars: {
        title: 'Calendriers de travail',
        subtitle: 'Les jours non travaillés, par site et par année',
        new: 'Nouveau calendrier', newTitle: 'Nouveau calendrier de travail',
        name: 'Nom', namePlaceholder: 'Calendrier Puigcerdà',
        year: 'Année', holidays: 'Jours fériés',
        site: 'Site', siteHint: 'Vide = toute l\'entreprise', allSites: 'Toute l\'entreprise',
        isDefault: 'C\'est le calendrier par défaut',
        isDefaultHint: 'Utilisé par ceux qui n\'en ont pas d\'autre. Un seul par année.',
        default: 'Par défaut',
        date: 'Date', holidayName: 'De quoi il s\'agit', unnamed: 'Sans nom',
        add: 'Ajouter', noHolidays: 'Pas encore de jours fériés. Ajoute-les un par un depuis les '
          + 'bulletins officiels : ils changent chaque année, c\'est pourquoi nous ne les '
          + 'remplissons pas à ta place.',
        scopes: { national: 'National', regional: 'Régional', local: 'Local', company: 'D\'entreprise' },
        preview: 'Jours fériés de {name}',
        copyForward: 'Copier vers {year}',
        copied: 'Calendrier {year} créé. Les jours se saisissent à la main : ils changent chaque année.',
        saved: 'Calendrier enregistré',
      },

      team: {
        title: 'Calendrier d\'équipe',
        subtitle: 'La semaine d\'une équipe, avec la couverture en dessous',
      },

      balances: {
        title: 'Soldes',
        subtitle: 'Combien de jours chacun a et combien il en a pris',
        entitled: 'Attribués', consumed: 'Pris',
        pending: 'Demandés', available: 'Restants',
        allocated: 'Attribution de l\'année', carriedOver: 'Reportés de l\'année précédente',
        carriedOverHint: 'Seulement si la convention permet de les reporter.',
        accrualFrom: 'Droit à partir du', accrualTo: 'Jusqu\'au',
        accrualHint: 'Pour les entrées et sorties en cours d\'année : l\'attribution est proratisée.',
        allocate: 'Attribuer des jours', allocateTitle: 'Attribution annuelle',
        adjust: 'Ajuster', adjustTitle: 'Ajuster le solde',
        currently: 'Actuellement, il lui reste {days} jours',
        days: 'Jours', daysPlaceholder: '+1 ou -1',
        reason: 'Motif', reasonHint: 'Obligatoire. Quelqu\'un qui n\'était pas là le lira dans deux ans.',
        adjusted: 'Solde ajusté',
        openFile: 'Ouvrir le dossier',
        rollOver: 'Passer à l\'année suivante', rollOverTitle: 'Reporter les soldes',
        rollOverLead: 'Les attributions de {from} sont copiées vers {to}. Les ajustements non : ils '
          + 'appartenaient à cette année-là.',
        carryUnused: 'Reporter les jours non pris',
        carryUnusedHint: 'Si la convention ne le dit pas, laisse désactivé : personne ne reporte '
          + 'par défaut, et les offrir est plus difficile à défaire que les accorder ensuite.',
        rolledOver: '{count} soldes créés pour {year}',
      },

      // ── La fiche professionnelle ────────────────────────────────────────
      people: {
        title: 'Personnel',
        subtitle: 'Les données d\'emploi sans lesquelles un planning ne se dessine pas',
        person: 'Personne', team: 'Équipe', site: 'Site',
        code: 'N°', contract: 'Contrat', category: 'Catégorie', manager: 'Responsable',
        total: 'Personnes', withEnd: 'Avec une date de sortie', unassigned: 'Sans catégorie',
        openEnded: 'Indéterminé', noContract: 'Aucune donnée',
        hoursShort: '{hours} h/sem',
        since: 'Depuis le {date}', until: 'Jusqu\'au {date}',
        endsIn: 'Se termine dans {days} jours', ended: 'Terminé',
        allCategories: 'Toutes les catégories',
        edit: 'Modifier la fiche', schedule: 'Voir son planning',
        managed: 'Géré dans le dossier',
        managedHint: 'Avec le module dossier du personnel installé, ces données se modifient '
          + 'là-bas et se lisent seulement ici. Deux écrans qui écrivent la même date d\'entrée, '
          + 'c\'est ainsi que les deux cessent de concorder.',
      },

      person: {
        title: 'Fiche professionnelle',
        editTitle: 'Données professionnelles',
        code: 'N° de salarié',
        contractType: 'Type de contrat',
        contractFrom: 'Entrée', contractTo: 'Sortie prévue',
        contractToHint: 'Vide = indéterminé. Hors de ces dates, personne n\'est planifié.',
        weeklyHours: 'Heures hebdomadaires',
        weeklyHoursHint: 'C\'est ce qui convertit une absence demandée en heures en fraction de journée.',
        workingWeek: 'Sa semaine de travail',
        workingWeekHint: 'Rien de coché = celle de l\'entreprise. Seulement pour qui n\'a pas de planning.',
        category: 'Catégorie',
        categoryHint: 'Comment on voit d\'un coup d\'œil qui peut couvrir un poste.',
        manager: 'Responsable',
        managerHint: 'À qui parviennent ses demandes d\'absence.',
        noManager: 'Personne en particulier',
        workCalendar: 'Calendrier de travail',
        workCalendarHint: 'Vide = le calendrier par défaut de l\'année.',
        defaultCalendar: 'Celui par défaut',
        notes: 'Notes', saved: 'Fiche enregistrée',
      },




      absenceTypes: {
        newTitle: 'Nouveau type d\'absence',
        name: 'Nom', code: 'Code',
        codeHint: 'Il ne pourra plus être changé.',
        codeLocked: 'Non modifiable : les absences déjà enregistrées le portent.',
        colour: 'Couleur',
        needs: 'Ce qu\'il faut', costs: 'Ce que cela coûte',
        allows: 'Comment on peut le demander', affects: 'Ce que cela touche',
        requiresApproval: 'Quelqu\'un doit l\'approuver',
        requiresReason: 'Il faut expliquer le motif',
        requiresDocument: 'Il faut joindre un justificatif',
        approvalDays: 'Délai pour répondre (jours)',
        approvalDaysHint: 'Sert à trier la boîte : ce qui dépasse le délai remonte en haut.',
        consumesBalance: 'Décompte des jours d\'un solde',
        balance: 'Solde',
        balanceConcept: 'De quel solde',
        balanceConceptHint: 'Un code. Deux types avec le même code partagent la même enveloppe.',
        isPaid: 'Elle est rémunérée',
        allowsHalfDay: 'On peut demander une demi-journée',
        allowsHours: 'On peut demander en heures',
        affectsPlanning: 'Retire la personne du planning',
        affectsPlanningHint: 'À désactiver pour la formation : quelqu\'un en stage est au travail, '
          + 'et le marquer absent fait paraître l\'équipe courte sans qu\'elle le soit.',
        affectsAttendance: 'Le pointage cesse d\'attendre son badgeage',
        affectsAttendanceHint: 'C\'est ce qui évite que des congés ressortent comme un pointage '
          + 'manquant. À désactiver uniquement pour l\'absence injustifiée : qu\'elle ne soit pas '
          + 'venue est précisément ce que le registre doit continuer à dire.',
        active: 'Actif', saved: 'Type enregistré',
        short: {
          approval: 'approbation', document: 'justificatif',
          works: 'reste au planning', attendance: 'compte au pointage',
        },
      },

      config: {
        title: 'Configuration',
        subtitle: 'Ce qui se décide une fois : postes, modèles, types et couverture',
        showArchived: 'Afficher les archivés',
        shifts: 'Postes', patterns: 'Modèles',
        absenceTypes: 'Types d\'absence',
        coverage: 'Couverture',
        new: {
          shifts: 'Nouveau poste', patterns: 'Nouveau modèle',
          absenceTypes: 'Nouveau type',
          coverage: 'Nouvelle couverture',
        },
      },

      empty: {
        noShifts: 'Aucun poste configuré pour l\'instant',
        noShiftsHint: 'Un poste est une tranche d\'horloge avec un nom : «Matin, de 06:00 à 14:00». '
          + 'Sans au moins un, il n\'y a rien à planifier.',
        createShift: 'Créer le premier poste',
        noPeople: 'Personne sur le planning',
        noPeopleHint: 'Les personnes viennent des utilisateurs de la plateforme. Vérifie dans les '
          + 'réglages quels rôles apparaissent ici.',
        noPatterns: 'Aucun modèle pour l\'instant',
        noPatternsHint: 'Un modèle est la forme d\'un planning sans dates ni personnes. C\'est ce '
          + 'qui transforme la planification d\'un mois pour douze personnes en un clic.',
        noRequests: 'Rien en attente',
        noRequestsHint: 'Quand quelqu\'un demandera une absence, elle apparaîtra ici.',
        noAbsences: 'Aucune absence sur cette période',
        noAbsencesHint: 'Essaie d\'élargir les dates ou d\'enlever un filtre.',
        noAbsencesForPerson: 'Rien d\'enregistré pour cette personne',
        noShiftsForMe: 'Tu n\'as aucun poste planifié',
        noShiftsForMeHint: 'Dès que le planning sera publié, ils apparaîtront ici.',
        noShiftsForPerson: 'Aucun poste sur cette période',
        noBalance: 'Tu n\'as pas encore de soldes',
        noBalanceHint: 'C\'est celui qui gère le personnel qui les attribue, une fois par an.',
        noBalances: 'Personne n\'a de solde de ce type',
        noBalancesHint: 'Attribue-les personne par personne, ou reporte ceux de l\'année dernière.',
        noCalendars: 'Aucun calendrier de travail',
        noCalendarsHint: 'Un calendrier, ce sont les jours fériés d\'un site et d\'une année. Sans '
          + 'lui, un jour férié se planifie comme n\'importe quel autre.',
        noCoverage: 'Aucune couverture minimale configurée',
        noCoverageHint: 'C\'est ce qui fait qu\'approuver des congés montre avec combien de '
          + 'personnes l\'équipe reste, au lieu de deux boutons et rien d\'autre.',
        noTeam: 'Choisis une équipe',
        noTeamHint: 'Les équipes viennent du module équipes. Sans lui, configure la couverture sur '
          + 'un site.',
      },

      notifications: {
        categories: {
          published: 'Planning publié',
          requested: 'Demandes d\'absence',
          decided: 'Réponse à ta demande',
          coverage: 'Un poste sous son minimum',
        },
        coverage: {
          title: 'Un poste reste sous son minimum',
          body: '{days} jour(s) sous le minimum. Le {date} : {available} sur {required}.',
        },
        published: {
          title: 'Le planning est sorti',
          body: 'La planification du {from} au {to} a été publiée.',
        },
        absence: {
          requested: {
            title: 'Demande de {type}',
            body: '{worker} demande du {from} au {to}.',
          },
          approved: { title: 'Approuvée', body: 'Ta {type} du {from} au {to} est approuvée.' },
          rejected: { title: 'Refusée', body: 'Ta {type} du {from} au {to} n\'a pas été approuvée.' },
          cancelled: { title: 'Annulée', body: 'Ta {type} du {from} au {to} a été annulée.' },
        },
        unplanned: {
          title: 'Trous la semaine prochaine',
          body: '{count} personnes n\'ont rien de prévu du {from} au {to}.',
        },
      },

      // Las tarjetas del asistente (backend/assistant.js): pedir, aprobar y
      // rechazar una ausencia.
      assistant: {
        request: {
          summary: 'Demander {type} du {from} au {to}',
          action: 'Demander une absence',
          after: 'Elle attend que quelqu’un l’approuve',
          outcome: 'Demandé : {type} du {from} au {to}',
        },
        field: {
          type: 'Type',
          from: 'Du',
          to: 'Au',
          reason: 'Motif',
          who: 'Qui',
          days: 'Jours',
          notes: 'Note',
        },
        approve: {
          summary: 'Approuver {type} de {who} du {from} au {to}',
          action: 'Approuver l’absence',
          outcome: 'Approuvée : {who}, du {from} au {to}',
          breach: 'Approuvée même si l’équipe reste sous son minimum',
        },
        reject: {
          summary: 'Refuser {type} de {who} du {from} au {to}',
          action: 'Refuser l’absence',
          outcome: 'Refusée : {who}, du {from} au {to}',
        },
        decide: {
          notFound: 'La demande n’est pas claire : choisissez-la dans Demandes',
          where: 'Demande',
        },
      },

      dashboard: {
        mine: 'Mes prochains postes',
        mineHelp: 'Les jours où tu travailles à partir d\'aujourd\'hui, avec l\'horaire. Publié seulement.',
        myBalance: 'Mes jours',
        myBalanceHelp: 'Ce qu\'il te reste de chaque enveloppe : congés, jours personnels, etc.',
        workingToday: 'Au travail aujourd\'hui',
        workingTodayHelp: 'Combien de personnes ont un poste aujourd\'hui et ne sont pas absentes.',
        absentToday: 'Absents aujourd\'hui',
        absentTodayHelp: 'Combien d\'absences approuvées couvrent la journée d\'aujourd\'hui.',
        unplanned: 'Non planifiés aujourd\'hui',
        unplannedHelp: 'Personnes dont rien n\'est dit aujourd\'hui : ni poste, ni repos, ni absence.',
        pending: 'Demandes en attente',
        pendingHelp: 'Combien d\'absences attendent une approbation ou un refus.',
        coverage: 'Équipes sous le minimum',
        coverageHelp: 'Combien de couvertures minimales ne sont pas tenues aujourd\'hui.',
        upcoming: 'Absences qui arrivent',
        upcomingHelp: 'Ce qui est approuvé et commence dans les prochains jours, tant qu\'il est temps.',
      },
    },

    modules: { workforce: 'Effectifs' },
    assistant: {
      openers: {
        workforce: {
          'when-do-i-work': 'Quand est-ce que je travaille cette semaine ?',
          'my-holidays': 'Combien de jours de congés me reste-t-il ?',
          'who-is-available': 'Sur qui puis-je compter lundi prochain ?',
          'pending-requests': 'Quelles demandes d’absence m’attendent ?',
        },
      },
    },
    permissions: {
      workforce: {
        me: 'Voir mon planning et demander des absences',
        read: 'Voir le planning et les absences de tous',
        plan: 'Affecter des postes et publier le planning',
        approve: 'Approuver et refuser les absences',
        manage: 'Configurer postes, calendriers, types et soldes',
        people: 'Consulter et modifier la fiche professionnelle',
      },
    },
    settings: {
      ns: {
        workforce: {
          title: 'Personnel',
          subtitle: 'Comment on planifie, comment on compte les absences et quels papiers on garde',
          groups: { planning: 'Planning', absences: 'Absences', people: 'Personnes' },
          weekStartsOn: {
            label: 'Le premier jour de la semaine',
            help: 'Décide où commence la grille et sur quelles semaines tourne un modèle.',
          },
          publishBeforeVisible: {
            label: 'Le planning n\'est visible qu\'une fois publié',
            help: 'Activé, on prépare le mois prochain sans que personne ne le voie. Désactivé, '
              + 'chaque changement apparaît aussitôt dans «Mon calendrier».',
          },
          minRestHours: {
            label: 'Heures de repos minimum entre deux postes',
            help: 'Douze est ce qu\'exige la loi espagnole. C\'est un avertissement, pas un refus.',
          },
          maxConsecutiveDays: {
            label: 'Jours d\'affilée avant d\'avertir',
            help: 'Également un avertissement. À zéro, rien n\'est vérifié.',
          },
          workingWeek: {
            label: 'Jours ouvrés de la semaine',
            help: '1 = lundi. C\'est le repli pour compter une '
              + 'absence de quelqu\'un sans planning.',
          },
          standardDayMinutes: {
            label: 'Minutes d\'une journée standard',
            help: 'Convertit une absence demandée en heures en fraction de journée.',
          },
          selfRequestAbsence: {
            label: 'Chacun peut demander ses absences',
            help: 'Désactivé, seul celui qui peut les approuver les saisit.',
          },
          approverRoles: {
            label: 'Rôles avertis d\'une demande',
            help: 'En plus du responsable de la personne, s\'il y en a un.',
          },
          contractTypes: {
            label: 'Types de contrat de cette entreprise',
            help: 'Ceux que propose la fiche professionnelle.',
          },
          categories: {
            label: 'Catégories professionnelles',
            help: 'Servent à exiger des documents par profil.',
          },
          workerRoles: {
            label: 'Rôles qui apparaissent sur le planning',
            help: 'C\'est de là que vient la liste à l\'installation.',
          },
          warnUnplannedWeek: {
            label: 'Avertir le vendredi si la semaine prochaine a des trous',
            help: 'Un avis à celui qui planifie, avec la liste de qui n\'a rien de prévu.',
          },
        },
      },
    },
    audit: {
      actions: {
        shift: {
          created: 'Poste créé',
          updated: 'Poste modifié',
          archived: 'Poste archivé',
          restored: 'Poste remis au catalogue',
        },
        pattern: {
          created: 'Modèle créé',
          updated: 'Modèle modifié',
          archived: 'Modèle archivé',
          restored: 'Modèle remis au catalogue',
        },
        calendar: {
          created: 'Calendrier de travail créé',
          updated: 'Calendrier de travail modifié',
          archived: 'Calendrier de travail archivé',
          restored: 'Calendrier de travail remis au catalogue',
        },
        absence_type: {
          created: 'Type d\'absence créé',
          updated: 'Type d\'absence modifié',
          deactivated: 'Type d\'absence désactivé',
        },
        plan: {
          assigned: 'Postes affectés',
          pattern_applied: 'Modèle appliqué',
          copied: 'Semaine copiée',
          cleared: 'Planning vidé',
          published: 'Planning publié',
          publish_notice_failed: 'Échec de l\'annonce de publication',
        },
        absence: {
          requested: 'Absence demandée',
          created: 'Absence enregistrée',
          updated: 'Absence modifiée',
          approved: 'Absence approuvée',
          rejected: 'Absence refusée',
          cancelled: 'Absence annulée',
        },
        balance: {
          allocated: 'Solde attribué',
          adjusted: 'Solde ajusté',
          rolled_over: 'Soldes reportés à l\'année suivante',
        },
        coverage: {
          set: 'Couverture minimale configurée',
          removed: 'Couverture minimale supprimée',
          overridden: 'Approuvé sous la couverture minimale',
        },
        people: {
          record_updated: 'Données professionnelles modifiées',
        },
      },
    },
    errors: {
      fields: {
        MANAGED_BY_PERSONNEL: 'Cette fiche est gérée par le module dossier. Modifiez-la là-bas.',
        INVALID_TIME: 'Écris-le sous la forme HH:MM',
        INVALID_DATE: 'Ce n’est pas une date',
        ENDS_BEFORE_IT_STARTS: 'Cela ne peut pas finir avant de commencer',
        BREAK_TOO_LONG: 'La pause ne peut pas durer autant que le poste',
        RANGE_TOO_LONG: 'La période est trop longue (maximum {max})',
        PARTIAL_NEEDS_ONE_DAY: 'Une absence partielle porte sur un seul jour',
        UNIT_NOT_ALLOWED: 'Ce type d\'absence ne peut pas être demandé ainsi',
        NOTHING_TO_ASSIGN: 'Il n\'y a rien à affecter',
        NOTHING_TO_COPY: 'Il n\'y a rien de planifié à copier',
        OVERLAPS_SOURCE: 'Cela doit commencer après ce qui est copié',
        SHIFT_NOT_FOUND: 'Cela pointe vers un poste qui n\'existe plus',
        TYPE_NOT_FOUND: 'Ce type n\'est pas disponible',
        WRONG_YEAR: 'Cette date n\'est pas de {year}',
        TOO_MANY: 'C\'est trop (maximum {max})',
      },
    },
  },
}

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'
// The reasons and field codes its errors carry (frontend/i18n/errorReasons.js).
import { withErrorReasons } from './i18n/errorReasons.js'

export const messages = withErrorReasons(withToolLabels(withAnalytics(baseMessages, 'workforce')))

export default { routes, navigation, messages }
